import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useHistory } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import toast from "react-hot-toast";
import { fetchPipelineOrders, updatePipelineStage } from "../../../../../services/orders";
import { fetchInstanceUsers } from "../../../../../services/users";
import OrderCartPanel from "../../orders/order-cart-panel/OrderCartPanel";
import { listPresentationsByOrder, sendPresentationEmail } from "../../../../../services/presentation";
import { fetchEstimatesForOrder, fetchSalesOrdersForOrder, sendEstimationEmail } from "../../../../../services/estimation.js";
import {
  assignProspect,
  completeCrmActivity,
  convertMarketingProspect,
  createCrmCardActivity,
  createCrmCardCommunication,
  createProspectOpportunity,
  dismissCrmUnmappedCommunication,
  fetchCrmCardActivity,
  fetchCrmCardCommunications,
  fetchCrmNotificationCandidates,
  fetchCrmPipelineBoard,
  fetchCrmUnmappedCommunications,
  generateCrmNotifications,
  getFriendlyApiError,
  getMarketingProspectConversionPreview,
  isInternalApiError,
  linkCrmUnmappedCommunication,
  reassignProspect,
  retryTransientRequest,
  previewCrmEmailTemplate,
  saveCrmEmailTemplateDraft,
  searchCrmCommunicationLinkTargets,
  unassignProspect,
  updateCrmCardTags,
} from "../../../../../services/crm";
import FastUpdateDialog from "./FastUpdateDialog";
import DocumentEmailDialog from "./DocumentEmailDialog";
import { currency, hasKnownMoneyValue, moneyValueLabel, shortDate } from "./pipelineCardFormatters";

export const ACTIVE_COLUMNS = [
  { key: "opportunity", label: "Opportunity", moveStatus: "Quote Requested", tone: "#2563eb", bg: "#eff6ff" },
  { key: "waiting", label: "Waiting", moveStatus: "Waiting", tone: "#d97706", bg: "#fffbeb" },
  { key: "presentation", label: "Presentation", moveStatus: "Presentation Draft", tone: "#7c3aed", bg: "#f5f3ff" },
  { key: "estimate", label: "Estimate", moveStatus: "Estimate Draft", tone: "#0f766e", bg: "#f0fdfa" },
  { key: "salesOrder", label: "Sales Order", moveStatus: "Agreement Pending", tone: "#15803d", bg: "#f0fdf4" },
];

const WIN_COLUMN = { key: "win", label: "WIN", readOnly: true, tone: "#15803d", bg: "#f0fdf4" };
const LOSS_COLUMN = { key: "loss", label: "LOSS", readOnly: true, tone: "#b91c1c", bg: "#fef2f2" };
const ALL_LEADS_COLUMN = { key: "commonPool", label: "All Leads", readOnly: false, tone: "#0891b2", bg: "#ecfeff" };
const MY_LEADS_COLUMN = { key: "myLeads", label: "My Leads", readOnly: false, tone: "#16a34a", bg: "#f0fdf4" };
const POST_SALE_STATUSES = ["In Production", "Shipped", "Ready to be Invoiced", "Invoiced", "Paid", "Closed"];
const WIN_STATUSES = ["Won", ...POST_SALE_STATUSES];
const TERMINAL_COLUMN_KEYS = new Set(["win", "loss"]);
const LOSS_REASONS = ["Price", "No response", "Competitor", "Customer cancelled", "Duplicate", "Other"];
const REVIVE_REASONS = ["Customer responded", "Pricing revised", "Reopened discussion", "Mistakenly marked lost", "Other"];
const STALE_CARD_MESSAGE = "This card was updated by another user. The board has been refreshed. Please try again.";
const AUTO_REFRESH_INTERVAL_MS = 60 * 1000;
const AUTO_REFRESH_DEBOUNCE_MS = 2000;
const DRAG_SCROLL_EDGE_PX = 80;
const DRAG_SCROLL_MAX_PX_PER_FRAME = 24;
const COLUMN_META = [ALL_LEADS_COLUMN, MY_LEADS_COLUMN, ...ACTIVE_COLUMNS, WIN_COLUMN, LOSS_COLUMN].reduce((acc, col) => ({ ...acc, [col.key]: col }), {});
const WORKSPACE_TABS = ["Overview", "Cart", "Presentation", "Estimate", "Sales Order", "Win/Loss", "Communication", "History"];
const WORKSPACE_TAB_INDEX = WORKSPACE_TABS.reduce((acc, label, index) => ({ ...acc, [label]: index }), {});
const newTabLinkProps = { target: "_blank", rel: "noopener noreferrer" };
const RAW_PROSPECT_TABS = ["Overview", "Source", "Assignment", "Convert", "Communication", "History"];
const LINKED_PROSPECT_TABS = ["Overview", "Source", "Assignment", "Convert", "Project", "Communication", "History"];
const CRM_TEMPLATE_CATEGORIES = new Set([
  "CRM Follow-up",
  "CRM Estimate Follow-up",
  "CRM Presentation Follow-up",
  "CRM Stale Opportunity",
  "CRM Re-engagement",
  "CRM Lost Opportunity Reopen",
  "CRM Meeting Reminder",
  "CRM Internal Rep Reminder",
]);
const CRM_MERGE_FIELD_LABELS = [
  "customerName",
  "companyName",
  "contactEmail",
  "contactPhone",
  "cardTitle",
  "crmStage",
  "ownerName",
  "orderNumber",
  "prospectReference",
  "estimateNumber",
  "lastActivityDate",
  "nextActivityDate",
  "trackingReference",
  "instanceName",
];

const initialWorkspaceTabForCard = (card) => {
  if (!card || card.cardType === "prospect") return WORKSPACE_TAB_INDEX.Overview;

  const lane = String(card.pipelineColumn || card.laneKey || "").toLowerCase();
  const status = String(card.soStatus || card.status || "").toLowerCase();

  if (TERMINAL_COLUMN_KEYS.has(lane) || ["won", "lost", "cancelled", "canceled"].includes(status)) {
    return WORKSPACE_TAB_INDEX["Win/Loss"];
  }
  if (
    lane === "salesorder" ||
    ["agreement pending", "agreement sent", "sales order ready"].includes(status)
  ) {
    return WORKSPACE_TAB_INDEX["Sales Order"];
  }
  if (lane === "estimate" || ["estimate draft", "estimate sent"].includes(status)) {
    return WORKSPACE_TAB_INDEX.Estimate;
  }
  if (lane === "presentation" || ["presentation draft", "presentation sent"].includes(status)) {
    return WORKSPACE_TAB_INDEX.Presentation;
  }
  return WORKSPACE_TAB_INDEX.Overview;
};

const emptyGroups = () => {
  const groups = {};
  ACTIVE_COLUMNS.forEach((col) => {
    groups[col.key] = { ...col, cards: [], count: 0, totalValue: 0, totalValueKnownCount: 0 };
  });
  groups.win = {
    ...WIN_COLUMN,
    cards: [],
    count: 0,
    totalValue: 0,
    totalValueKnownCount: 0,
    sections: WIN_STATUSES.reduce((acc, status) => {
      acc[status] = { status, cards: [], count: 0, totalValue: 0, totalValueKnownCount: 0 };
      return acc;
    }, {}),
  };
  groups.loss = {
    ...LOSS_COLUMN,
    cards: [],
    count: 0,
    totalValue: 0,
    totalValueKnownCount: 0,
  };
  return groups;
};

const assignedNames = (assignedTo = []) =>
  (Array.isArray(assignedTo) ? assignedTo : [])
    .map((u) => u.name || [u.first_name, u.last_name].filter(Boolean).join(" ").trim())
    .filter(Boolean);

const userName = (user) =>
  [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
  user?.name ||
  user?.email ||
  (user?.id ? `User #${user.id}` : "");

const userNameById = (users, id) => {
  if (!id) return "None";
  const user = (users || []).find((entry) => Number(entry.id) === Number(id));
  return user ? userName(user) : `User #${id}`;
};

const titleize = (value) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase()) || "N/a";

const dateTimeLabel = (value) => {
  if (!value) return "n/a";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "n/a" : d.toLocaleString();
};

const toDateTimeLocalValue = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60000);
  return local.toISOString().slice(0, 16);
};

// Fallbacks used when a card has no stage-specific accountability policy.
const DEFAULT_NEXT_ACTIVITY_POLICY = {
  activityType: "follow_up",
  dueOffsetHours: 24,
};

const nextActivityPolicyForCard = (card) => ({
  ...DEFAULT_NEXT_ACTIVITY_POLICY,
  ...(card?.accountabilityPolicy || {}),
});

const defaultNextActivityDue = (policy = DEFAULT_NEXT_ACTIVITY_POLICY) => {
  const d = new Date();
  const configuredOffset = Number(policy.defaultDueOffsetHours ?? policy.dueOffsetHours);
  const dueOffsetHours = Number.isFinite(configuredOffset)
    ? configuredOffset
    : DEFAULT_NEXT_ACTIVITY_POLICY.dueOffsetHours;
  d.setTime(d.getTime() + dueOffsetHours * 60 * 60 * 1000);
  return toDateTimeLocalValue(d);
};

const activityRouteIdForCard = (card) => {
  if (!card) return "";
  if (card.cardType === "prospect") return card.entityId;
  return card.orderPrimaryId || card.meta?.orderPrimaryId || card.orderId || card.entityId;
};

const cartOrderIdForCard = (card) =>
  card?.orderId || card?.projectNumber || card?.meta?.orderId || card?.entityId;

const attentionLabel = (flag) => {
  if (flag === "overdue_activity") return "OVERDUE";
  if (flag === "no_next_activity") return "NO NEXT ACTIVITY";
  return titleize(flag).toUpperCase();
};

const ATTENTION_FILTERS = [
  { key: "needs_attention", label: "Needs Attention" },
  { key: "red_only", label: "Red Only" },
  { key: "overdue", label: "Overdue" },
  { key: "no_next_activity", label: "No Next Activity" },
];

const accountabilityTone = (ageDays, policy = {}) => {
  const age = Number(ageDays);
  const red = policy.redAgeDays === null || policy.redAgeDays === undefined || policy.redAgeDays === ""
    ? null
    : Number(policy.redAgeDays);
  const yellow = policy.yellowAgeDays === null || policy.yellowAgeDays === undefined || policy.yellowAgeDays === ""
    ? null
    : Number(policy.yellowAgeDays);
  if (Number.isFinite(age) && Number.isFinite(red) && age >= red) return "danger";
  if (Number.isFinite(age) && Number.isFinite(yellow) && age >= yellow) return "warning";
  return "neutral";
};

const estimateAccountabilityTone = (card) => {
  const buckets = Array.isArray(card?.accountabilityPolicy?.estimateAgeBuckets)
    ? card.accountabilityPolicy.estimateAgeBuckets
    : [];
  const index = buckets.findIndex((bucket) => bucket?.label === card?.estimateAgeBucket);
  if (index === -1 || buckets.length < 2) return "neutral";
  if (index === buckets.length - 1) return "danger";
  if (index === buckets.length - 2) return "warning";
  return "neutral";
};

const accountabilityBadges = (card) => {
  const badges = [];
  const flags = Array.isArray(card?.attentionFlags) ? card.attentionFlags : [];
  if (flags.includes("overdue_activity")) {
    badges.push({ key: "overdue_activity", label: attentionLabel("overdue_activity"), tone: "critical" });
  }
  if (flags.includes("no_next_activity")) {
    badges.push({ key: "no_next_activity", label: attentionLabel("no_next_activity"), tone: "danger" });
  }
  if (card?.stageAgeDays != null) {
    badges.push({
      key: "stage_age",
      label: `STAGE ${card.stageAgeDays}D`,
      tone: accountabilityTone(card.stageAgeDays, card.accountabilityPolicy),
    });
  }
  if (card?.estimateAgeBucket && card.estimateAgeBucket !== "none") {
    badges.push({
      key: "estimate_age",
      label: `ESTIMATE ${card.estimateAgeBucket}`,
      tone: estimateAccountabilityTone(card),
    });
  }
  return badges;
};

const cardMatchesAttentionFilter = (card, filter) => {
  if (!filter) return true;
  const flags = Array.isArray(card?.attentionFlags) ? card.attentionFlags : [];
  const badges = accountabilityBadges(card);
  if (filter === "needs_attention") return flags.length > 0;
  if (filter === "red_only") return badges.some((badge) => ["critical", "danger"].includes(badge.tone));
  if (filter === "overdue") return flags.includes("overdue_activity");
  if (filter === "no_next_activity") return flags.includes("no_next_activity");
  return true;
};

const cardsFromGroups = (groups = {}) => {
  const seen = new Set();
  const cards = [];
  Object.values(groups).forEach((group) => {
    (group?.cards || []).forEach((card) => {
      const id = String(cardDomId(card));
      if (!id || seen.has(id)) return;
      seen.add(id);
      cards.push(card);
    });
  });
  return cards;
};

const filterGroupsByAttention = (groups, filter, tagId = "") => {
  if (!filter && !tagId) return groups;
  const matches = (card) => cardMatchesAttentionFilter(card, filter) && (!tagId || (card.tags || []).some((tag) => String(tag.id) === String(tagId)));
  return Object.entries(groups || {}).reduce((result, [key, group]) => {
    const cards = (group?.cards || []).filter(matches);
    const sections = group?.sections
      ? Object.entries(group.sections).reduce((sectionResult, [sectionKey, section]) => {
          const sectionCards = (section?.cards || []).filter(matches);
          sectionResult[sectionKey] = { ...section, cards: sectionCards, count: sectionCards.length };
          return sectionResult;
        }, {})
      : group?.sections;
    result[key] = {
      ...group,
      cards,
      count: sections ? Object.values(sections).reduce((total, section) => total + (section.cards?.length || 0), 0) : cards.length,
      sections,
    };
    return result;
  }, {});
};

const accountabilityBadgeSx = (tone, workspace = false) => {
  const tones = {
    critical: { color: "#fff", backgroundColor: "#b91c1c", borderColor: "#991b1b" },
    danger: { color: "#991b1b", backgroundColor: "#fee2e2", borderColor: "#ef4444" },
    warning: { color: "#9a3412", backgroundColor: "#ffedd5", borderColor: "#f97316" },
    neutral: { color: "#475569", backgroundColor: "#f8fafc", borderColor: "#cbd5e1" },
  };
  return {
    ...(tones[tone] || tones.neutral),
    height: workspace ? 25 : 21,
    border: "1px solid",
    borderRadius: 1,
    fontSize: workspace ? 11.5 : 10.5,
    fontWeight: 900,
    letterSpacing: 0,
    "& .MuiChip-label": { px: workspace ? 1.1 : 0.8 },
  };
};

function AccountabilitySummary({ card }) {
  const badges = accountabilityBadges(card);
  const policy = card?.accountabilityPolicy;
  if (!badges.length && !policy) return null;
  return (
    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.25, backgroundColor: "#f8fafc" }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} gap={1}>
        <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Accountability</Typography>
        {policy ? (
          <Typography variant="caption" color="text.secondary">
            Target {policy.stageTargetAgeDays ?? "n/a"}d | Yellow {policy.yellowAgeDays ?? "n/a"}d | Red {policy.redAgeDays ?? "n/a"}d
          </Typography>
        ) : null}
      </Stack>
      {badges.length ? (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
          {badges.map((badge) => (
            <Chip key={badge.key} size="small" label={badge.label} sx={accountabilityBadgeSx(badge.tone, true)} />
          ))}
        </Stack>
      ) : null}
      {(card?.attentionFlags || []).some((flag) => ["overdue_activity", "no_next_activity"].includes(flag)) ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Resolve the next-activity policy warning before moving this card when practical. Stage movement is not blocked.
        </Alert>
      ) : null}
    </Box>
  );
}

const cardOwnerName = (card) => card?.assignedToName || assignedNames(card?.assignedTo).join(", ") || "Unassigned";

function TagChips({ tags = [], limit = 3 }) {
  if (!tags.length) return null;
  const visibleTags = tags.slice(0, limit);
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {visibleTags.map((tag) => (
        <Chip key={tag.id} size="small" label={tag.name} sx={{ height: 20, fontSize: 10.5, fontWeight: 800, color: tag.color, borderColor: tag.color, backgroundColor: `${tag.color}12` }} variant="outlined" />
      ))}
      {tags.length > limit ? <Chip size="small" label={`+${tags.length - limit}`} sx={{ height: 20, fontSize: 10.5 }} /> : null}
    </Stack>
  );
}

function AttentionFilterField({ label, children }) {
  return (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800, lineHeight: 1 }}>
        {label}
      </Typography>
      {children}
    </Stack>
  );
}

function AttentionDetail({ label, value }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 700, lineHeight: 1.1 }}>{label}</Typography>
      <Typography variant="body2" sx={{ mt: 0.25, fontWeight: 700, lineHeight: 1.3, overflowWrap: "anywhere" }}>{value}</Typography>
    </Box>
  );
}

function NeedsAttentionDialog({ open, onClose, cards, onOpenCard, scopeLabel, availableTags = [] }) {
  const [ownerFilter, setOwnerFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [warningFilter, setWarningFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [entityFilter, setEntityFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [sortBy, setSortBy] = useState("score");
  const owners = useMemo(() => [...new Set(cards.map(cardOwnerName))].sort(), [cards]);
  const stages = useMemo(() => [...new Set(cards.map((card) => card.pipelineColumnLabel || titleize(card.pipelineColumn)))].sort(), [cards]);
  const filteredCards = useMemo(() => cards.filter((card) => {
    const flags = card.attentionFlags || [];
    const badges = accountabilityBadges(card);
    return (!ownerFilter || cardOwnerName(card) === ownerFilter)
      && (!stageFilter || (card.pipelineColumnLabel || titleize(card.pipelineColumn)) === stageFilter)
      && (!warningFilter || flags.includes(warningFilter))
      && (severityFilter !== "red" || badges.some((badge) => ["critical", "danger"].includes(badge.tone)))
      && (!entityFilter || card.cardType === entityFilter)
      && (!tagFilter || (card.tags || []).some((tag) => String(tag.id) === tagFilter));
  }).sort((a, b) => {
    if (sortBy === "due") {
      const aDue = a.nextActivity?.dueAt ? new Date(a.nextActivity.dueAt).getTime() : Number.MAX_SAFE_INTEGER;
      const bDue = b.nextActivity?.dueAt ? new Date(b.nextActivity.dueAt).getTime() : Number.MAX_SAFE_INTEGER;
      return aDue - bDue || Number(b.attentionScore || 0) - Number(a.attentionScore || 0);
    }
    return Number(b.attentionScore || 0) - Number(a.attentionScore || 0);
  }), [cards, entityFilter, ownerFilter, severityFilter, sortBy, stageFilter, tagFilter, warningFilter]);
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: "calc(100vh - 64px)", borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontWeight: 900, pb: 1.5 }}>Manager Needs Attention</DialogTitle>
      <DialogContent dividers sx={{ p: 0, overflow: "hidden", display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Box sx={{ p: 2, pb: 1.75, flexShrink: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: "text.secondary", mb: 1.5 }}>
            {filteredCards.length} of {cards.length} cards needing attention in {scopeLabel}.
          </Typography>
          <Box sx={{ p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 2, backgroundColor: "#f8fafc" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1.25 }}>Filter and sort</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))", gap: 1.25 }}>
              <AttentionFilterField label="Owner"><TextField select SelectProps={{ native: true }} size="small" value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)} inputProps={{ "aria-label": "Owner filter" }} fullWidth><option value="">All owners</option>{owners.map((owner) => <option key={owner} value={owner}>{owner}</option>)}</TextField></AttentionFilterField>
              <AttentionFilterField label="Stage"><TextField select SelectProps={{ native: true }} size="small" value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} inputProps={{ "aria-label": "Stage filter" }} fullWidth><option value="">All stages</option>{stages.map((stage) => <option key={stage} value={stage}>{stage}</option>)}</TextField></AttentionFilterField>
              <AttentionFilterField label="Warning"><TextField select SelectProps={{ native: true }} size="small" value={warningFilter} onChange={(e) => setWarningFilter(e.target.value)} inputProps={{ "aria-label": "Warning filter" }} fullWidth><option value="">All warnings</option><option value="overdue_activity">Overdue activity</option><option value="no_next_activity">No next activity</option><option value="stage_aging">Stage aging</option><option value="estimate_aging">Estimate aging</option></TextField></AttentionFilterField>
              <AttentionFilterField label="Severity"><TextField select SelectProps={{ native: true }} size="small" value={severityFilter} onChange={(e) => setSeverityFilter(e.target.value)} inputProps={{ "aria-label": "Severity filter" }} fullWidth><option value="all">All warnings</option><option value="red">Red only</option></TextField></AttentionFilterField>
              <AttentionFilterField label="Card type"><TextField select SelectProps={{ native: true }} size="small" value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} inputProps={{ "aria-label": "Card type filter" }} fullWidth><option value="">All types</option><option value="prospect">Prospects</option><option value="order">Orders</option></TextField></AttentionFilterField>
              <AttentionFilterField label="Tag"><TextField select SelectProps={{ native: true }} size="small" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} inputProps={{ "aria-label": "Tag filter" }} fullWidth><option value="">All tags</option>{availableTags.map((tag) => <option key={tag.id} value={tag.id}>{tag.name}</option>)}</TextField></AttentionFilterField>
              <AttentionFilterField label="Sort by"><TextField select SelectProps={{ native: true }} size="small" value={sortBy} onChange={(e) => setSortBy(e.target.value)} inputProps={{ "aria-label": "Sort attention cards" }} fullWidth><option value="score">Attention score</option><option value="due">Due date</option></TextField></AttentionFilterField>
            </Box>
          </Box>
        </Box>
        <Stack spacing={1.25} sx={{ px: 2, pb: 2, overflowY: "auto", minHeight: 0, scrollbarGutter: "stable", ...scrollbarSx }}>
          {filteredCards.length ? filteredCards.map((card) => {
            const badges = accountabilityBadges(card).filter((badge) => badge.tone !== "neutral");
            const owner = cardOwnerName(card);
            const name = card.cardType === "prospect"
              ? (card.contactName || card.customerName || card.companyName || card.projectName)
              : (card.customerName || card.projectName);
            return (
              <Box key={cardDomId(card)} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: { xs: 1.5, sm: 1.75 }, backgroundColor: "#fff", boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "minmax(0, 1fr) auto" }, gap: 1.5, alignItems: "center" }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.25 }}>{name || "Unnamed card"}</Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" }, gap: 1.25, mt: 1.25 }}>
                      <AttentionDetail label="Stage" value={card.pipelineColumnLabel || titleize(card.pipelineColumn)} />
                      <AttentionDetail label="Owner" value={owner} />
                      <AttentionDetail label="Attention score" value={card.attentionScore || 0} />
                      <AttentionDetail label="Next activity due" value={card.nextActivity?.dueAt ? dateTimeLabel(card.nextActivity.dueAt) : "None"} />
                    </Box>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                      {badges.map((badge) => (
                        <Chip key={badge.key} size="small" label={badge.label} sx={accountabilityBadgeSx(badge.tone)} />
                      ))}
                    </Stack>
                    {card.tags?.length ? <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75 }}><Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Tags</Typography><TagChips tags={card.tags} /></Stack> : null}
                  </Box>
                  <Button variant="contained" size="small" onClick={() => onOpenCard(card)} sx={{ justifySelf: { xs: "start", sm: "end" }, minWidth: 104, flexShrink: 0 }}>
                    Open Card
                  </Button>
                </Box>
              </Box>
            );
          }) : <Alert severity="success">No cards match the selected attention filters.</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

function ReportBlock({ title, rows }) {
  return (
    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>{title}</Typography>
      <Stack spacing={0.5}>{rows.length ? rows.map(([label, value]) => <Stack key={label} direction="row" justifyContent="space-between" gap={2}><Typography variant="body2">{label}</Typography><Typography variant="body2" sx={{ fontWeight: 900 }}>{value}</Typography></Stack>) : <Typography variant="body2" color="text.secondary">No data</Typography>}</Stack>
    </Box>
  );
}

const countBy = (cards, labelFor) => Object.entries(cards.reduce((counts, card) => {
  const label = labelFor(card);
  if (label) counts[label] = (counts[label] || 0) + 1;
  return counts;
}, {})).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));

function ManagementReportsDialog({ open, onClose, cards, scopeLabel }) {
  const now = Date.now();
  const openCards = cards.filter((card) => !["win", "loss"].includes(card.pipelineColumn));
  const attentionCards = cards.filter((card) => (card.attentionFlags || []).length);
  const dueSoonCards = cards.filter((card) => {
    const dueAt = card.nextActivity?.dueAt ? new Date(card.nextActivity.dueAt).getTime() : null;
    return dueAt && dueAt >= now && dueAt <= now + 24 * 60 * 60 * 1000;
  });
  const warningRows = [
    ["Overdue activities", cards.filter((card) => card.attentionFlags?.includes("overdue_activity")).length],
    ["No next activity", cards.filter((card) => card.attentionFlags?.includes("no_next_activity")).length],
    ["Stage aging", cards.filter((card) => card.attentionFlags?.includes("stage_aging")).length],
    ["Estimate aging", cards.filter((card) => card.attentionFlags?.includes("estimate_aging")).length],
    ["Red warnings", cards.filter((card) => accountabilityBadges(card).some((badge) => ["critical", "danger"].includes(badge.tone))).length],
    ["Yellow warnings", cards.filter((card) => accountabilityBadges(card).some((badge) => badge.tone === "warning")).length],
  ];
  const groupSx = { display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1.5 };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>CRM Management Reports</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>Reports are based on currently loaded board cards. Scope: {scopeLabel}. They are not historical exports.</Alert>
        <Stack spacing={2.5}>
          <Box><Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>Pipeline Health</Typography><Box sx={groupSx}><ReportBlock title="Cards by stage" rows={countBy(cards, (card) => card.pipelineColumnLabel || titleize(card.pipelineColumn))} /><ReportBlock title="Open cards by owner" rows={countBy(openCards, cardOwnerName)} /><ReportBlock title="Pipeline warning summary" rows={warningRows} /></Box></Box>
          <Box><Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>Follow-up Accountability</Typography><Box sx={groupSx}><ReportBlock title="Follow-up status" rows={[["Overdue activities", cards.filter((card) => card.attentionFlags?.includes("overdue_activity")).length], ["No next activity", cards.filter((card) => card.attentionFlags?.includes("no_next_activity")).length], ["Due within 24 hours", dueSoonCards.length]]} /><ReportBlock title="Owner-wise needs attention" rows={countBy(attentionCards, cardOwnerName)} /></Box></Box>
          <Box><Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>Aging</Typography><Box sx={groupSx}><ReportBlock title="Stage aging by stage" rows={countBy(cards.filter((card) => card.attentionFlags?.includes("stage_aging")), (card) => card.pipelineColumnLabel || titleize(card.pipelineColumn))} /><ReportBlock title="Estimate aging" rows={countBy(cards.filter((card) => card.estimateAgeBucket && card.estimateAgeBucket !== "none"), (card) => card.estimateAgeBucket)} /><ReportBlock title="Stale opportunities" rows={[["Red stage threshold", cards.filter((card) => accountabilityTone(card.stageAgeDays, card.accountabilityPolicy) === "danger").length]]} /></Box></Box>
          <Box><Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>Outcome</Typography><Box sx={groupSx}><ReportBlock title="Won / Loss" rows={[["Won", cards.filter((card) => card.pipelineColumn === "win").length], ["Lost / Cancelled", cards.filter((card) => card.pipelineColumn === "loss").length]]} /><ReportBlock title="Document flow snapshot" rows={[["Estimate stage", cards.filter((card) => card.pipelineColumn === "estimate").length], ["Sales Order stage", cards.filter((card) => card.pipelineColumn === "salesOrder").length]]} /></Box></Box>
          <Box><Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>Tags</Typography><Box sx={groupSx}><ReportBlock title="Tag-wise open cards" rows={countBy(openCards.flatMap((card) => (card.tags || []).map((tag) => ({ tag: tag.name }))), (entry) => entry.tag)} /><ReportBlock title="Tag-wise needs attention" rows={countBy(attentionCards.flatMap((card) => (card.tags || []).map((tag) => ({ tag: tag.name }))), (entry) => entry.tag)} /></Box></Box>
        </Stack>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

function buildNotificationCandidates(cards) {
  const now = Date.now();
  return cards.flatMap((card) => {
    const alerts = [];
    const owner = cardOwnerName(card);
    const add = (type, label, message, severity) => alerts.push({ card, type, label, message, severity, owner });
    if (card.attentionFlags?.includes("overdue_activity")) add("activity_overdue", "Activity overdue", `Next activity was due ${dateTimeLabel(card.nextActivity?.dueAt)}.`, "red");
    else if (card.nextActivity?.dueAt && new Date(card.nextActivity.dueAt).getTime() <= now + 24 * 60 * 60 * 1000) add("activity_due_soon", "Activity due soon", `Due ${dateTimeLabel(card.nextActivity.dueAt)}.`, "yellow");
    if (card.attentionFlags?.includes("no_next_activity")) add("no_next_activity", "No next activity", "Policy requires a next activity.", "red");
    const stageTone = accountabilityTone(card.stageAgeDays, card.accountabilityPolicy);
    if (card.attentionFlags?.includes("stage_aging")) add("stage_aging", "Card stuck in stage", `${card.stageAgeDays} days in ${card.pipelineColumnLabel || titleize(card.pipelineColumn)}.`, stageTone === "danger" ? "red" : "yellow");
    const estimateTone = estimateAccountabilityTone(card);
    if (card.attentionFlags?.includes("estimate_aging")) add("estimate_aging", "Estimate aging", `Estimate age is ${card.estimateAgeBucket}.`, estimateTone === "danger" ? "red" : "yellow");
    if (Number(card.attentionScore || 0) >= 75) add("manager_escalation_candidate", "Manager escalation candidate", `Attention score ${card.attentionScore}.`, "red");
    return alerts;
  }).sort((a, b) => (a.severity === b.severity ? Number(b.card.attentionScore || 0) - Number(a.card.attentionScore || 0) : a.severity === "red" ? -1 : 1));
}

function NotificationCandidatesDialog({ open, onClose, cards, onOpenCard, instanceUniqueId, canGenerate = false }) {
  const alerts = useMemo(() => buildNotificationCandidates(cards), [cards]);
  const [serverPreview, setServerPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [generating, setGenerating] = useState(false);
  const serverSendable = serverPreview?.candidates?.filter((candidate) => candidate.canSend).length || 0;
  const loadPreview = useCallback(async () => {
    if (!open || !canGenerate || !instanceUniqueId) return;
    try {
      setLoadingPreview(true);
      const result = await fetchCrmNotificationCandidates(instanceUniqueId, { limit: 50 });
      setServerPreview(result);
    } catch (error) {
      toast.error(getFriendlyApiError(error, "Could not preview CRM notification delivery."));
    } finally {
      setLoadingPreview(false);
    }
  }, [canGenerate, instanceUniqueId, open]);
  useEffect(() => { loadPreview(); }, [loadPreview]);
  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const result = await generateCrmNotifications(instanceUniqueId, { limit: 50 });
      toast.success(`Generated ${result.created || 0} CRM alert${result.created === 1 ? "" : "s"}. ${result.skipped || 0} duplicate${result.skipped === 1 ? "" : "s"} skipped.`);
      await loadPreview();
    } catch (error) {
      toast.error(getFriendlyApiError(error, "Could not generate CRM notifications."));
    } finally {
      setGenerating(false);
    }
  };
  return <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth><DialogTitle sx={{ fontWeight: 900 }}>CRM Notification Candidates</DialogTitle><DialogContent dividers><Alert severity="info" sx={{ mb: 1.5 }}>CRM alerts use Elite's existing notification bell. Delivery is in-app only; schedules, digests, external channels, customer notifications, and automation remain disabled.</Alert>{canGenerate ? <Alert severity="success" sx={{ mb: 1.5 }}>{loadingPreview ? "Checking sendability..." : `${serverSendable} server-validated candidate${serverSendable === 1 ? "" : "s"} can be sent now. Duplicate candidates are skipped by dedupe key.`}</Alert> : <Alert severity="warning" sx={{ mb: 1.5 }}>Only managers and administrators can generate CRM notifications.</Alert>}<Stack spacing={1}>{alerts.length ? alerts.map((alert, index) => <Box key={`${cardDomId(alert.card)}-${alert.type}-${index}`} sx={{ border: "1px solid", borderColor: alert.severity === "red" ? "error.light" : "warning.light", borderRadius: 2, p: 1.25 }}><Stack direction="row" justifyContent="space-between" gap={1}><Box><Typography variant="subtitle2" sx={{ fontWeight: 900 }}>{alert.label} | {alert.owner}</Typography><Typography variant="body2">{alert.card.customerName || alert.card.contactName || alert.card.projectName}</Typography><Typography variant="caption" color="text.secondary">{alert.message}</Typography><Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>Can send through existing notification system after server validation.</Typography></Box><Button size="small" onClick={() => onOpenCard(alert.card)}>Open</Button></Stack></Box>) : <Alert severity="success">No notification candidates in this board view.</Alert>}</Stack></DialogContent><DialogActions>{canGenerate ? <Button variant="contained" onClick={handleGenerate} disabled={generating || loadingPreview || serverSendable === 0}>{generating ? "Generating..." : "Generate Rep Alerts"}</Button> : null}<Button onClick={onClose}>Close</Button></DialogActions></Dialog>;
}

function UnmappedCommunicationsDialog({ open, onClose, instanceUniqueId, onResolved }) {
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: "unmapped", q: "", provider: "", direction: "" });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [selected, setSelected] = useState(null);
  const [targetQuery, setTargetQuery] = useState("");
  const [targets, setTargets] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [targetLoading, setTargetLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadRows = useCallback(async (page = 1) => {
    if (!open || !instanceUniqueId) return;
    try {
      setLoading(true);
      const result = await fetchCrmUnmappedCommunications(instanceUniqueId, { ...appliedFilters, page, limit: pagination.limit });
      setRows(Array.isArray(result.communications) ? result.communications : []);
      setPagination(result.pagination || { page, limit: pagination.limit, total: 0, totalPages: 1 });
    } catch (error) {
      toast.error(getFriendlyApiError(error, "Could not load unmapped communications."));
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, instanceUniqueId, open, pagination.limit]);

  useEffect(() => {
    if (open) loadRows(1);
  }, [loadRows, open]);

  const runTargetSearch = async () => {
    if (targetQuery.trim().length < 2) {
      setTargets([]);
      return;
    }
    try {
      setTargetLoading(true);
      const result = await searchCrmCommunicationLinkTargets(instanceUniqueId, targetQuery.trim(), { limit: 12 });
      setTargets(Array.isArray(result.targets) ? result.targets : []);
    } catch (error) {
      toast.error(getFriendlyApiError(error, "Could not search CRM cards."));
    } finally {
      setTargetLoading(false);
    }
  };

  const handleLink = async () => {
    if (!selected || !selectedTarget) {
      toast.error("Choose an unmapped item and CRM card.");
      return;
    }
    const targetLabel = `${titleize(selectedTarget.cardType)} - ${selectedTarget.title || selectedTarget.reference}`;
    if (!window.confirm(`Link this communication to ${targetLabel}?`)) return;
    try {
      setSubmitting(true);
      await linkCrmUnmappedCommunication(instanceUniqueId, selected.id, {
        cardType: selectedTarget.cardType,
        cardId: selectedTarget.routeId || selectedTarget.id,
      });
      toast.success("Unmapped communication linked.");
      setSelected(null);
      setSelectedTarget(null);
      setTargets([]);
      setTargetQuery("");
      await loadRows(1);
      await onResolved?.();
    } catch (error) {
      toast.error(getFriendlyApiError(error, "Could not link communication."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDismiss = async () => {
    if (!selected) return;
    const reason = window.prompt("Reason for dismissing this communication:");
    if (!reason?.trim()) return;
    try {
      setSubmitting(true);
      await dismissCrmUnmappedCommunication(instanceUniqueId, selected.id, reason.trim());
      toast.success("Unmapped communication dismissed.");
      setSelected(null);
      await loadRows(1);
    } catch (error) {
      toast.error(getFriendlyApiError(error, "Could not dismiss communication."));
    } finally {
      setSubmitting(false);
    }
  };

  const updateFilter = (field, value) => setFilters((current) => ({ ...current, [field]: value }));
  const applyFilters = () => {
    const nextFilters = { ...filters };
    setAppliedFilters(nextFilters);
    if (JSON.stringify(nextFilters) === JSON.stringify(appliedFilters)) loadRows(1);
  };
  const selectedResolved = selected && selected.status !== "unmapped";
  const hasRows = rows.length > 0;
  const showEmptyState = !loading && !hasRows;
  const filterFieldSx = {
    minWidth: 0,
    "& .MuiInputBase-root": { height: 40, backgroundColor: "#fff" },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { maxHeight: "calc(100vh - 48px)" } }}>
      <DialogTitle sx={{ fontWeight: 900 }}>Unmapped Emails</DialogTitle>
      <DialogContent
        dividers
        sx={{
          display: "grid",
          gridTemplateColumns: !hasRows ? "1fr" : { xs: "1fr", md: "minmax(0, 1.15fr) minmax(340px, 0.85fr)" },
          gap: 2,
          minHeight: !hasRows ? 420 : 560,
          alignItems: "stretch",
        }}
      >
        <Stack spacing={1.25} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "minmax(120px, 0.8fr) minmax(130px, 0.8fr) minmax(150px, 1fr)",
                md: "130px 140px 160px minmax(220px, 1fr) auto",
              },
              gap: 1,
              alignItems: "center",
            }}
          >
            <TextField select SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} size="small" label="Status" value={filters.status} onChange={(event) => updateFilter("status", event.target.value)} sx={filterFieldSx}>
              <option value="unmapped">New</option>
              <option value="mapped">Linked</option>
              <option value="ignored">Dismissed</option>
              <option value="all">All</option>
            </TextField>
            <TextField select SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} size="small" label="Direction" value={filters.direction} onChange={(event) => updateFilter("direction", event.target.value)} sx={filterFieldSx}>
              <option value="">All</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
            </TextField>
            <TextField size="small" label="Provider" value={filters.provider} onChange={(event) => updateFilter("provider", event.target.value)} sx={filterFieldSx} />
            <TextField size="small" label="Search" value={filters.q} onChange={(event) => updateFilter("q", event.target.value)} sx={{ ...filterFieldSx, gridColumn: { xs: "1", sm: "1 / -1", md: "auto" } }} />
            <Button variant="outlined" onClick={applyFilters} disabled={loading} sx={{ height: 40, px: 2.25, justifySelf: { xs: "stretch", md: "end" } }}>{loading ? "Loading..." : "Apply"}</Button>
          </Box>
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 180 }}>
              <CircularProgress size={24} />
            </Box>
          ) : null}
          {showEmptyState ? (
            <Box
              sx={{
                minHeight: 270,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                border: "1px dashed",
                borderColor: "divider",
                borderRadius: 1.5,
                backgroundColor: "#f8fafc",
                px: 3,
              }}
            >
              <Box sx={{ maxWidth: 460 }}>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>No unmapped emails found</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  When synced emails cannot be matched to a CRM card, they will appear here for review.
                </Typography>
              </Box>
            </Box>
          ) : null}
          {hasRows ? (
            <Stack spacing={1} sx={{ overflowY: "auto", pr: 0.5 }}>
              {rows.map((row) => (
                <Box key={row.id} onClick={() => { setSelected(row); setSelectedTarget(null); }} sx={{ border: "1px solid", borderColor: selected?.id === row.id ? "primary.main" : "divider", borderRadius: 1.5, p: 1.25, cursor: "pointer", backgroundColor: selected?.id === row.id ? "#eff6ff" : "#fff" }}>
                  <Stack direction="row" justifyContent="space-between" gap={1}>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      <Chip size="small" color={row.status === "unmapped" ? "warning" : row.status === "mapped" ? "success" : "default"} label={titleize(row.statusLabel || row.status)} />
                      {row.direction ? <Chip size="small" variant="outlined" label={titleize(row.direction)} /> : null}
                      {row.provider ? <Chip size="small" variant="outlined" label={titleize(row.provider)} /> : null}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">{activityTimestamp(row.receivedAt || row.createdAt)}</Typography>
                  </Stack>
                  <Typography variant="subtitle2" sx={{ mt: 0.75, fontWeight: 900, overflowWrap: "anywhere" }}>{row.subject || "(no subject)"}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25, overflowWrap: "anywhere" }}>
                    From: {row.fromEmail || "n/a"} | To: {row.toEmails?.join(", ") || "n/a"} | Mailbox: {row.mailbox || "n/a"}
                  </Typography>
                  {row.preview ? <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, overflowWrap: "anywhere" }}>{communicationBodyPreview(row.preview, 180)}</Typography> : null}
                </Box>
              ))}
            </Stack>
          ) : null}
          {hasRows ? (
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 0.25 }}>
              <Typography variant="caption" color="text.secondary">{pagination.total || 0} total</Typography>
              <Stack direction="row" spacing={1}>
                <Button size="small" disabled={loading || pagination.page <= 1} onClick={() => loadRows(pagination.page - 1)}>Previous</Button>
                <Button size="small" disabled={loading || pagination.page >= pagination.totalPages} onClick={() => loadRows(pagination.page + 1)}>Next</Button>
              </Stack>
            </Stack>
          ) : null}
        </Stack>
        {hasRows ? (
          <Stack spacing={1.5} sx={{ minWidth: 0 }}>
            {!selected ? (
              <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.5, backgroundColor: "#f8fafc" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Select an unmapped email</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Select an unmapped email to review and link it to a CRM card.
                </Typography>
              </Box>
            ) : (
            <>
              <Box>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                  <Chip size="small" label={titleize(selected.statusLabel || selected.status)} />
                  {selected.direction ? <Chip size="small" variant="outlined" label={titleize(selected.direction)} /> : null}
                  {selected.provider ? <Chip size="small" variant="outlined" label={titleize(selected.provider)} /> : null}
                </Stack>
                <Typography variant="h6" sx={{ fontWeight: 900, overflowWrap: "anywhere" }}>{selected.subject || "(no subject)"}</Typography>
              </Box>
              <InfoGrid rows={[
                ["Received", activityTimestamp(selected.receivedAt || selected.createdAt)],
                ["From", selected.fromEmail],
                ["To", selected.toEmails?.join(", ")],
                ["CC", selected.ccEmails?.join(", ")],
                ["Mailbox", selected.mailbox],
                ["External message", selected.externalMessageId],
                ["Thread", selected.threadId],
                ["Linked target", selected.linkedEntity ? `${titleize(selected.linkedEntity.cardType)} #${selected.linkedEntity.entityId}` : ""],
              ]} />
              <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.25, backgroundColor: "#f8fafc", maxHeight: 190, overflowY: "auto" }}>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{selected.body || selected.preview || "No message body was recorded."}</Typography>
              </Box>
              {selectedResolved ? <Alert severity="success">This item has already been resolved.</Alert> : (
                <Stack spacing={1.25}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Link to CRM Card</Typography>
                  <Stack direction="row" spacing={1}>
                    <TextField size="small" label="Search cards" value={targetQuery} onChange={(event) => setTargetQuery(event.target.value)} fullWidth onKeyDown={(event) => { if (event.key === "Enter") runTargetSearch(); }} />
                    <Button variant="outlined" onClick={runTargetSearch} disabled={targetLoading}>{targetLoading ? "Searching..." : "Search"}</Button>
                  </Stack>
                  <Stack spacing={0.75} sx={{ maxHeight: 190, overflowY: "auto" }}>
                    {targets.map((target) => (
                      <Box key={`${target.cardType}-${target.id}`} onClick={() => setSelectedTarget(target)} sx={{ border: "1px solid", borderColor: selectedTarget?.cardType === target.cardType && selectedTarget?.id === target.id ? "primary.main" : "divider", borderRadius: 1.5, p: 1, cursor: "pointer", backgroundColor: selectedTarget?.cardType === target.cardType && selectedTarget?.id === target.id ? "#eff6ff" : "#fff" }}>
                        <Stack direction="row" justifyContent="space-between" gap={1}>
                          <Typography variant="body2" sx={{ fontWeight: 900, overflowWrap: "anywhere" }}>{target.title}</Typography>
                          <Chip size="small" label={titleize(target.cardType)} />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                          {target.customer || "No customer"} | {target.email || target.phone || "No contact"} | {target.status || target.stage || "No status"} | {target.owner || "Unassigned"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{target.reference || ""}</Typography>
                      </Box>
                    ))}
                  </Stack>
                  <Stack direction="row" spacing={1}>
                    <Button variant="contained" onClick={handleLink} disabled={submitting || !selectedTarget}>{submitting ? "Linking..." : "Link to CRM Card"}</Button>
                    <Button color="warning" variant="outlined" onClick={handleDismiss} disabled={submitting}>Dismiss</Button>
                  </Stack>
                </Stack>
              )}
            </>
            )}
          </Stack>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function TagEditor({ card, tags = [], instanceUniqueId, onRefreshBoard }) {
  const [selectedIds, setSelectedIds] = useState((card?.tags || []).map((tag) => Number(tag.id)));
  const [saving, setSaving] = useState(false);
  useEffect(() => setSelectedIds((card?.tags || []).map((tag) => Number(tag.id))), [card?.cardId, card?.tags]);
  const toggle = (id) => setSelectedIds((current) => current.includes(id) ? current.filter((value) => value !== id) : [...current, id]);
  const save = async () => {
    try {
      setSaving(true);
      await updateCrmCardTags(instanceUniqueId, card.cardType, activityRouteIdForCard(card), selectedIds);
      toast.success("CRM tags updated.");
      await onRefreshBoard?.();
    } catch (error) { toast.error(getFriendlyApiError(error, "Could not update CRM tags.")); } finally { setSaving(false); }
  };
  return <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, p: 1.5 }}><Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ mb: 1 }}><Box><Typography variant="subtitle2" sx={{ fontWeight: 900 }}>CRM Tags</Typography><Typography variant="caption" color="text.secondary">Select active tags managed in CRM Settings.</Typography></Box><Button size="small" variant="contained" onClick={save} disabled={saving}>{saving ? "Saving..." : "Save"}</Button></Stack><Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>{tags.map((tag) => <Chip key={tag.id} clickable label={tag.name} variant={selectedIds.includes(Number(tag.id)) ? "filled" : "outlined"} onClick={() => toggle(Number(tag.id))} sx={selectedIds.includes(Number(tag.id)) ? { color: "#fff", backgroundColor: tag.color } : { color: tag.color, borderColor: tag.color }} />)}{!tags.length ? <Alert severity="info">No active tags. A manager can create tags in CRM Settings.</Alert> : null}</Stack></Box>;
}

const previewFieldEntries = (value) => {
  if (!value || typeof value !== "object") return [];
  return Object.entries(value)
    .filter(([, item]) => item !== null && item !== undefined && item !== "")
    .slice(0, 10);
};

const orderPath = (instanceUniqueId, orderId, tab = "overview", tabId = null) =>
  `/instance/${instanceUniqueId}/sales/orders/${orderId}/${tab}${tabId ? `/${tabId}` : ""}`;

const prospectReviewPath = (instanceUniqueId, prospectId) =>
  `/instance/${instanceUniqueId}/sales/prospects?openProspect=${prospectId}`;

const cardDomId = (card) => card?.cardId || card?.orderId || card?.entityId || "";

const isProspectCard = (card) => card?.cardType === "prospect";
const isOrderCard = (card) => !card?.cardType || card.cardType === "order";
const canDragPipelineCard = (card, isCrmMode = false) => {
  if (!card) return false;
  if (isCrmMode && isProspectCard(card)) return false;
  if (isOrderCard(card)) return !card.readOnly && !TERMINAL_COLUMN_KEYS.has(card.pipelineColumn);
  return false;
};

const crmInvalidDropReason = (card, targetColumnKey) => {
  if (!isProspectCard(card)) return null;
  if (card.pipelineColumn === "commonPool") {
    return "Managers must assign All Leads before they can move forward.";
  }
  return "Convert/link this prospect and create an opportunity/project before moving to document stages.";
};

const normalizeCrmCard = (card) => {
  if (card.cardType === "prospect") {
    return {
      ...card,
      orderId: card.cardId,
      projectNumber: card.entityId,
      projectName: card.title || "Untitled prospect",
      customerName: card.companyName || card.customerName || card.contactName || "No company",
      totalValue: card.value,
      soStatus: card.status || "unassigned",
      statusChip: card.status || "unassigned",
      pipelineColumn: card.laneKey || "myLeads",
      pipelineColumnLabel: COLUMN_META[card.laneKey]?.label || "My Leads",
      assignedTo: card.assignedToName ? [{ id: card.assignedToUserId, name: card.assignedToName }] : [],
      readOnly: true,
      draggable: false,
      linkedOrderId: card.meta?.linkedOrderId || null,
      linkedOrderBusinessId: card.linkedOrderBusinessId || card.meta?.linkedOrderBusinessId || card.meta?.linkedOrderNumber || null,
      linkedOrderNumber: card.linkedOrderNumber || card.meta?.linkedOrderNumber || card.meta?.linkedOrderBusinessId || null,
      linkedOrderProjectName: card.linkedOrderProjectName || card.meta?.linkedOrderProjectName || null,
      leadIndicator: card.leadIndicator || card.meta?.leadIndicator || null,
    };
  }

  if (card.cardType === "order") {
    return {
      ...card,
      orderId: card.meta?.orderId || card.entityId,
      projectNumber: card.meta?.projectNumber || card.entityId,
      projectName: card.title || "Untitled project",
      customerName: card.customerName || card.companyName || "No customer",
      totalValue: card.value,
      soStatus: card.status,
      statusChip: card.status,
      orderStatus: card.meta?.orderStatus || {},
      inHandsDate: card.meta?.inHandsDate || null,
      assignedTo: card.meta?.assignedTo || [],
      pipelineColumn: card.laneKey || "opportunity",
      pipelineColumnLabel: COLUMN_META[card.laneKey]?.label || "Opportunity",
      updatedAt: card.meta?.updatedAt,
      crmPipelineRank: card.rank,
      crmPipelineRankUpdatedAt: card.meta?.crmPipelineRankUpdatedAt,
      leadIndicator: card.leadIndicator || card.meta?.leadIndicator || null,
      readOnly: Boolean(card.readOnly || TERMINAL_COLUMN_KEYS.has(card.laneKey)),
    };
  }

  return card;
};

const normalizeCrmBoardData = (data = {}) => {
  const groups = emptyCrmGroups();
  Object.entries(data.groups || {}).forEach(([key, group]) => {
    const meta = COLUMN_META[key] || group || {};
    groups[key] = {
      ...groups[key],
      ...group,
      ...meta,
      cards: Array.isArray(group.cards) ? group.cards.map(normalizeCrmCard) : [],
      sections: group.sections
        ? Object.entries(group.sections).reduce((acc, [sectionKey, section]) => {
            acc[sectionKey] = {
              ...section,
              cards: Array.isArray(section.cards) ? section.cards.map(normalizeCrmCard) : [],
            };
            return acc;
          }, {})
        : groups[key]?.sections,
    };
  });
  return groups;
};

const groupValueLabel = (group) => {
  if (!group) return "\u2014";
  if (Number.isFinite(Number(group.totalValueKnownCount))) {
    return Number(group.totalValueKnownCount) > 0 ? currency(group.totalValue) : "\u2014";
  }
  return Array.isArray(group.cards) && group.cards.some((card) => hasKnownMoneyValue(card.totalValue))
    ? currency(group.totalValue)
    : "\u2014";
};

const summaryValueLabel = (summary) => {
  if (!summary) return "\u2014";
  if (Number.isFinite(Number(summary.totalValueKnownCount))) {
    return Number(summary.totalValueKnownCount) > 0 ? currency(summary.totalValue) : "\u2014";
  }
  return hasKnownMoneyValue(summary.totalValue) ? currency(summary.totalValue) : "\u2014";
};

const clonePipelineGroup = (group = {}) => ({
  ...group,
  cards: Array.isArray(group.cards) ? [...group.cards] : [],
  sections: group.sections
    ? Object.entries(group.sections).reduce((acc, [key, section]) => {
        acc[key] = { ...section, cards: Array.isArray(section.cards) ? [...section.cards] : [] };
        return acc;
      }, {})
    : group.sections,
});

const removeCardFromTerminalSections = (group, card) => {
  if (!group?.sections || !card) return group;
  const sections = {};
  Object.entries(group.sections).forEach(([key, section]) => {
    const cards = Array.isArray(section.cards)
      ? section.cards.filter((item) => String(item.orderId) !== String(card.orderId))
      : [];
    const removed = (section.cards?.length || 0) - cards.length;
    const removedValueKnown = removed && hasKnownMoneyValue(card.totalValue);
    sections[key] = {
      ...section,
      cards,
      count: removed ? Math.max(0, (section.count || 0) - removed) : section.count,
      totalValue: removed
        ? Math.max(0, Number(section.totalValue || 0) - Number(card.totalValue || 0))
        : section.totalValue,
      totalValueKnownCount: removedValueKnown
        ? Math.max(0, Number(section.totalValueKnownCount || 0) - 1)
        : section.totalValueKnownCount,
    };
  });
  return { ...group, sections };
};

const emptyCrmGroups = () => {
  const groups = emptyGroups();
  return {
    commonPool: { ...ALL_LEADS_COLUMN, cards: [], count: 0, totalValue: 0, totalValueKnownCount: 0 },
    myLeads: { ...MY_LEADS_COLUMN, cards: [], count: 0, totalValue: 0, totalValueKnownCount: 0 },
    ...groups,
  };
};

const findCardInGroups = (groups, cardId) => {
  for (const [columnKey, group] of Object.entries(groups || {})) {
    if (!Array.isArray(group.cards)) continue;
    const index = group.cards.findIndex((card) => String(cardDomId(card)) === String(cardId));
    if (index !== -1) return { card: group.cards[index], columnKey, index };
  }
  return null;
};

const cardsForColumn = (groups, columnKey) => {
  const group = groups?.[columnKey];
  return Array.isArray(group?.cards) ? group.cards : [];
};

const moveCardInGroups = (groups, cardId, toColumnKey, toStatus, targetIndex = 0, rankPatch = {}) => {
  const source = findCardInGroups(groups, cardId);
  if (!source || !groups?.[toColumnKey]) return groups;

  const sourceColumnKey = source.columnKey;
  const sameColumn = sourceColumnKey === toColumnKey;
  const next = { ...groups };
  let sourceGroup = clonePipelineGroup(groups[sourceColumnKey]);
  let targetGroup = sameColumn ? sourceGroup : clonePipelineGroup(groups[toColumnKey]);
  const [moved] = sourceGroup.cards.splice(source.index, 1);
  if (!moved) return groups;
  const movedHasKnownValue = hasKnownMoneyValue(moved.totalValue);

  sourceGroup = removeCardFromTerminalSections(sourceGroup, moved);
  if (!sameColumn) {
    sourceGroup = {
      ...sourceGroup,
      count: Math.max(0, (sourceGroup.count || 0) - 1),
      totalValue: Math.max(0, Number(sourceGroup.totalValue || 0) - Number(moved.totalValue || 0)),
      totalValueKnownCount: movedHasKnownValue
        ? Math.max(0, Number(sourceGroup.totalValueKnownCount || 0) - 1)
        : sourceGroup.totalValueKnownCount,
    };
  }

  const movedCard = {
    ...moved,
    pipelineColumn: toColumnKey,
    pipelineColumnLabel: COLUMN_META[toColumnKey]?.label || moved.pipelineColumnLabel,
    soStatus: toStatus,
    statusChip: toStatus,
    readOnly: false,
    orderStatus: { ...(moved.orderStatus || {}), SO: toStatus },
    crmPipelineRank: rankPatch.crmPipelineRank ?? moved.crmPipelineRank ?? null,
    crmPipelineRankUpdatedAt: rankPatch.crmPipelineRankUpdatedAt ?? moved.crmPipelineRankUpdatedAt ?? null,
    updatedAt: rankPatch.updatedAt ?? moved.updatedAt ?? null,
  };

  const boundedIndex = Math.max(0, Math.min(Number(targetIndex) || 0, targetGroup.cards.length));
  targetGroup.cards.splice(boundedIndex, 0, movedCard);
  if (!sameColumn) {
    targetGroup = {
      ...targetGroup,
      count: (targetGroup.count || 0) + 1,
      totalValue: Number(targetGroup.totalValue || 0) + Number(movedCard.totalValue || 0),
      totalValueKnownCount: movedHasKnownValue
        ? Number(targetGroup.totalValueKnownCount || 0) + 1
        : targetGroup.totalValueKnownCount,
    };
  }

  next[sourceColumnKey] = sourceGroup;
  next[toColumnKey] = targetGroup;
  return next;
};

export const getPipelineTransitionIntent = ({ fromColumnId, toColumnId, fromStatus, toStatus } = {}) => {
  if (TERMINAL_COLUMN_KEYS.has(fromColumnId) || TERMINAL_COLUMN_KEYS.has(toColumnId)) {
    return { type: "readonly", requiresFastPopup: false, fromStatus, toStatus };
  }
  if (fromColumnId === toColumnId) {
    return { type: "status_only", requiresFastPopup: false, fromStatus, toStatus };
  }
  const popupTargets = {
    presentation: "presentation",
    estimate: "estimate",
    salesOrder: "sales_order",
  };
  return {
    type: popupTargets[toColumnId] || toColumnId || "status_only",
    requiresFastPopup: Boolean(popupTargets[toColumnId]),
    fromStatus,
    toStatus,
  };
};

const documentTypeForTargetColumn = (columnKey) => ({
  presentation: "presentation",
  estimate: "estimate",
  salesOrder: "salesOrder",
}[columnKey] || null);

const stopCardAction = (event) => {
  event.preventDefault();
  event.stopPropagation();
};

const stopCardPointer = (event) => {
  event.stopPropagation();
};

const removeDragImageNode = (node) => {
  if (node?.parentNode) node.parentNode.removeChild(node);
};

const buildPipelineDragImage = (sourceNode, card) => {
  const rect = sourceNode?.getBoundingClientRect?.();
  const width = Math.max(260, Math.round(rect?.width || 292));
  const clone = sourceNode?.cloneNode?.(true);

  const node = clone || document.createElement("div");
  node.setAttribute("data-pipeline-drag-image", "true");
  node.style.position = "fixed";
  node.style.top = "-10000px";
  node.style.left = "-10000px";
  node.style.width = `${width}px`;
  node.style.maxWidth = `${width}px`;
  node.style.pointerEvents = "none";
  node.style.opacity = "1";
  node.style.transform = "rotate(1.2deg)";
  node.style.zIndex = "99999";
  node.style.boxSizing = "border-box";
  node.style.boxShadow = "0 18px 40px rgba(15, 23, 42, 0.24)";
  node.style.background = "#fff";

  if (!clone) {
    const meta = COLUMN_META[card.pipelineColumn] || COLUMN_META.opportunity;
    node.style.padding = "10px";
    node.style.border = `1px solid ${meta.tone}`;
    node.style.borderRadius = "8px";
    node.innerHTML = `
      <div style="font-size:11px;font-weight:800;color:#64748b;">#${card.projectNumber || card.orderId || ""}</div>
      <div style="font-size:13px;font-weight:900;color:#0f172a;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${card.projectName || "Untitled project"}</div>
      <div style="font-size:12px;color:#64748b;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${card.customerName || "No customer"}</div>
      <div style="margin-top:8px;display:inline-flex;height:22px;align-items:center;padding:0 8px;border-radius:6px;border:1px solid ${meta.tone}33;background:${meta.bg};color:${meta.tone};font-size:11px;font-weight:800;">${card.statusChip || card.soStatus || "Opportunity"}</div>
    `;
  }

  document.body.appendChild(node);
  return node;
};

const PipelineCard = memo(function PipelineCard({
  card,
  columnKey,
  index,
  isDragging = false,
  isCrmMode = false,
  onOpenTab,
  onUnclaimProspect,
  currentUserId,
  isCrmManager = false,
  disableDragging = false,
  onNativeDragStart,
  onNativeCardDragOver,
  onNativeDragEnd,
}) {
  const meta = COLUMN_META[card.pipelineColumn] || COLUMN_META.opportunity;
  const names = assignedNames(card.assignedTo);
  const isProspect = isProspectCard(card);
  const conversionStatus = String(card.meta?.conversionStatus || "").toLowerCase();
  const canReturnProspect = !["converted", "linked"].includes(conversionStatus) && !card.meta?.linkedCustomerId;
  const canUnclaim = isProspect && isCrmManager && canReturnProspect && card.assignedToUserId;
  const canDragCard = !disableDragging && canDragPipelineCard(card, isCrmMode);
  const canOpenCard = true;
  const leadIndicator = card.leadIndicator || card.meta?.leadIndicator || null;
  const leadIndicatorColor = leadIndicator?.color === "green" ? "#22c55e" : leadIndicator?.color === "blue" ? "#2563eb" : null;
  const cardTitle = canDragCard ? "Drag to move stage" : canOpenCard ? (isProspect ? "Open lead details" : "Open details") : "Unavailable";
  const crmAccountabilityBadges = accountabilityBadges(card);
  const style = {
    opacity: isDragging ? 0 : 1,
  };
  const openTab = (tabIndex) => onOpenTab(card, tabIndex);
  const openFromButton = (event, tabIndex) => {
    stopCardAction(event);
    openTab(tabIndex);
  };

  return (
    <Box
      component="article"
      data-pipeline-card="true"
      data-card-id={cardDomId(card)}
      data-card-index={index}
      title={cardTitle}
      draggable={canDragCard}
      style={style}
      onDragStart={(event) => onNativeDragStart(event, card, columnKey, index)}
      onDragOver={(event) => onNativeCardDragOver(event, card, columnKey, index)}
      onDragEnd={onNativeDragEnd}
      onClick={() => onOpenTab(card)}
      sx={{
        p: 1.15,
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "rgba(148, 163, 184, 0.34)",
        backgroundColor: "#fff",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        cursor: canDragCard ? (isDragging ? "grabbing" : "grab") : canOpenCard ? "pointer" : "not-allowed",
        transformOrigin: "center",
        transition: isDragging ? "none" : "border-color 120ms ease, background-color 120ms ease, box-shadow 120ms ease, transform 120ms ease",
        userSelect: "none",
        touchAction: "manipulation",
        position: "relative",
        contain: "layout paint style",
        "&:hover": {
          borderColor: meta.tone,
          backgroundColor: "#fcfdff",
          boxShadow: canDragCard
            ? "0 8px 18px rgba(15, 23, 42, 0.12)"
            : "0 5px 14px rgba(15, 23, 42, 0.09)",
          transform: "translateY(-1px)",
        },
        "&:active": {
          cursor: canDragCard ? "grabbing" : canOpenCard ? "pointer" : "not-allowed",
          transform: "translateY(0)",
        },
        "& button, & a": {
          cursor: "pointer",
        },
      }}
    >
      <Stack spacing={0.85}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
              {isProspect ? `${card.source || "insightly"} ${card.meta?.externalType || "prospect"}` : `#${card.projectNumber || card.orderId}`}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.25, ...truncate }}>
              {card.projectName || "Untitled project"}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="flex-end" flexWrap="wrap" useFlexGap sx={{ minWidth: 0, maxWidth: 152 }}>
            {canDragCard ? (
              <Box
                component="span"
                aria-hidden="true"
                sx={{
                  color: "text.disabled",
                  fontSize: 14,
                  fontWeight: 900,
                  letterSpacing: 0,
                  lineHeight: 1,
                  userSelect: "none",
                }}
              >
                ::
              </Box>
            ) : null}
            {leadIndicatorColor ? (
              <Box
                component="span"
                title={leadIndicator?.label}
                aria-label={leadIndicator?.label}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  flex: "0 0 auto",
                  backgroundColor: leadIndicatorColor,
                  border: "1px solid #fff",
                  boxShadow: `0 0 0 2px ${leadIndicatorColor}33, 0 0 8px ${leadIndicatorColor}66`,
                }}
              />
            ) : null}
            <Box component="span" sx={{ ...pillSx, color: meta.tone, backgroundColor: meta.bg, borderColor: `${meta.tone}33`, maxWidth: 126 }}>
              {card.statusChip || card.soStatus || "Opportunity"}
            </Box>
          </Stack>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={truncate}>
          {isProspect ? (card.contactName || card.email || card.phone || "No contact details") : (card.customerName || "No customer")}
        </Typography>

        {isProspect ? (
          <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
            <Box component="span" sx={metricSx}>{card.email || "No email"}</Box>
            {card.phone ? <Box component="span" sx={metricSx}>{card.phone}</Box> : null}
          </Stack>
        ) : (
          <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
            <Box component="span" sx={metricSx}>{moneyValueLabel(card.totalValue)}</Box>
            <Box component="span" sx={metricSx}><CalendarTodayRoundedIcon sx={{ fontSize: 13 }} /> {shortDate(card.inHandsDate)}</Box>
          </Stack>
        )}

        <Typography variant="caption" color="text.secondary" sx={truncate}>
          {isProspect ? (card.assignedToName || "Unassigned prospect") : (names.length ? names.slice(0, 2).join(", ") : "No assigned rep")}
        </Typography>

        {crmAccountabilityBadges.length ? (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {crmAccountabilityBadges.slice(0, 4).map((badge) => (
              <Chip key={badge.key} size="small" label={badge.label} sx={accountabilityBadgeSx(badge.tone)} />
            ))}
          </Stack>
        ) : null}

        <TagChips tags={card.tags} limit={2} />

        {!isProspect ? <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {card.presentationCount > 0 && <MiniDocChip label={`P ${card.presentationCount}`} onClick={(event) => openFromButton(event, WORKSPACE_TAB_INDEX.Presentation)} />}
          {card.estimateCount > 0 && <MiniDocChip label={`E ${card.estimateCount}`} onClick={(event) => openFromButton(event, WORKSPACE_TAB_INDEX.Estimate)} />}
          {card.salesOrderCount > 0 && <MiniDocChip label={`SO ${card.salesOrderCount}`} onClick={(event) => openFromButton(event, WORKSPACE_TAB_INDEX["Sales Order"])} />}
          {card.purchaseOrderSummary?.count > 0 && <MiniDocChip label={`PO ${card.purchaseOrderSummary.count}`} onClick={(event) => openFromButton(event, WORKSPACE_TAB_INDEX["Win/Loss"])} />}
        </Stack> : null}

        <Stack direction="row" spacing={1}>
          {canUnclaim ? (
            <Button size="small" variant="outlined" color="warning" onPointerDown={stopCardPointer} onClick={(event) => { stopCardAction(event); onUnclaimProspect?.(card); }} sx={actionButtonSx}>
              Unassign
            </Button>
          ) : null}
          {!isProspect ? <Button size="small" variant="outlined" onPointerDown={stopCardPointer} onClick={(event) => openFromButton(event, WORKSPACE_TAB_INDEX.Cart)} sx={actionButtonSx}>
            Cart
          </Button> : null}
          {!isProspect && card.linkedCustomerId ? (
            <Button
              size="small"
              onPointerDown={stopCardPointer}
              onClick={(event) => openFromButton(event, WORKSPACE_TAB_INDEX.Overview)}
              sx={actionButtonSx}
            >
              Customer
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
});

const MiniDocChip = memo(function MiniDocChip({ label, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onPointerDown={onClick ? stopCardPointer : undefined}
      onClick={onClick}
      sx={{
        height: 20,
        border: "1px solid rgba(148, 163, 184, 0.35)",
        borderRadius: 1,
        px: 0.65,
        backgroundColor: "#f8fafc",
        color: "#334155",
        fontSize: 11,
        fontWeight: 800,
        lineHeight: "18px",
        cursor: onClick ? "pointer" : "default",
        fontFamily: "inherit",
      }}
    >
      {label}
    </Box>
  );
});

const PipelineLane = memo(function PipelineLane({ column, group, isDragOver = false, onNativeLaneDragOver, onNativeLaneDrop, onLoadMore, loadingMore, loadError, onRetry, children }) {
  return (
    <Box
      onDragOver={(event) => onNativeLaneDragOver(event, column.key)}
      onDrop={(event) => onNativeLaneDrop(event, column.key)}
      sx={{
        minWidth: 292,
        height: "100%",
        border: "1px solid",
        borderColor: isDragOver ? column.tone : "rgba(148, 163, 184, 0.28)",
        borderRadius: 2.5,
        backgroundColor: column.bg,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        opacity: column.readOnly ? 0.88 : 1,
        transition: "border-color 120ms ease, box-shadow 120ms ease",
        boxShadow: isDragOver ? `0 0 0 3px ${column.tone}1f` : "none",
      }}
    >
      <Stack sx={{ p: 1.35, pb: 1, position: "sticky", top: 0, zIndex: 1, backgroundColor: "#fff" }} spacing={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 8, height: 28, borderRadius: 99, backgroundColor: column.tone }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
                {column.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {groupValueLabel(group)}
              </Typography>
            </Box>
          </Stack>
          <Chip size="small" label={`${group?.cards?.length || 0} / ${group?.totalCount ?? group?.count ?? 0} loaded`} sx={{ fontWeight: 900, backgroundColor: "#fff" }} />
        </Stack>
        {column.readOnly ? (
          <Typography variant="caption" color="text.secondary">
            Read-only lifecycle statuses
          </Typography>
        ) : null}
      </Stack>
      <Stack
        onScroll={(event) => {
          const node = event.currentTarget;
          if (node.scrollHeight - node.scrollTop - node.clientHeight < 160) onLoadMore?.();
        }}
        spacing={1.15}
        sx={{
          flex: 1,
          minHeight: 0,
          p: 1.15,
          overflowY: "auto",
          overscrollBehaviorY: "auto",
          scrollbarGutter: "stable",
          ...scrollbarSx,
        }}
      >
        {children}
        {loadingMore ? <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ py: 1 }}><CircularProgress size={18} /><Typography variant="caption">Loading more...</Typography></Stack> : null}
        {loadError ? <Alert severity="error" action={<Button size="small" onClick={onRetry}>Retry</Button>}>{loadError}</Alert> : null}
        {!loadingMore && !loadError && group?.cards?.length > 0 && !group?.hasMore ? <Typography variant="caption" color="text.secondary" align="center" sx={{ py: 1 }}>End of lane</Typography> : null}
      </Stack>
    </Box>
  );
});

function LoadingLanes() {
  return (
    <Box sx={boardSx}>
      {[...ACTIVE_COLUMNS, WIN_COLUMN, LOSS_COLUMN].map((col) => (
        <Box key={col.key} sx={{ minWidth: 292, border: "1px solid rgba(148,163,184,.28)", borderRadius: 2.5, p: 1.25, backgroundColor: "#fff" }}>
          <Skeleton width="55%" height={30} />
          <Stack spacing={1.1} sx={{ mt: 1 }}>
            {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={138} sx={{ borderRadius: 2 }} />)}
          </Stack>
        </Box>
      ))}
    </Box>
  );
}

function EmptyLane() {
  return (
    <Box sx={{ p: 2.5, border: "1px dashed rgba(148,163,184,.55)", borderRadius: 2, textAlign: "center", backgroundColor: "rgba(255,255,255,.56)" }}>
      <FolderOpenRoundedIcon sx={{ color: "text.disabled", mb: 0.5 }} />
      <Typography variant="body2" color="text.secondary">No cards</Typography>
    </Box>
  );
}

function WorkspaceModal({
  card,
  initialTab = 0,
  onClose,
  instanceUniqueId,
  onAssignProspect,
  onReassignProspect,
  onUnclaimProspect,
  onRefreshBoard,
  onReviveLoss,
  currentUserId,
  isCrmManager = false,
  users = [],
  availableTags = [],
}) {
  const [tab, setTab] = useState(initialTab);
  const [assignmentUserId, setAssignmentUserId] = useState("");
  const [assignmentReason, setAssignmentReason] = useState("");
  const [assignmentUser, setAssignmentUser] = useState(null);
  const [assignmentUsers, setAssignmentUsers] = useState([]);
  const [assignmentOpen, setAssignmentOpen] = useState(false);
  const [assignmentInput, setAssignmentInput] = useState("");
  const [assignmentSearch, setAssignmentSearch] = useState("");
  const [assignmentLoading, setAssignmentLoading] = useState(false);
  const [assignmentPage, setAssignmentPage] = useState(0);
  const [assignmentTotal, setAssignmentTotal] = useState(0);
  const assignmentRequest = useRef(0);
  const assignmentBusy = useRef(false);
  const assignmentListbox = useRef(null);
  const assignmentScrollTop = useRef(0);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoadedFor, setHistoryLoadedFor] = useState(null);
  const [historyError, setHistoryError] = useState("");
  const [conversionPreview, setConversionPreview] = useState(null);
  const [conversionLoading, setConversionLoading] = useState(false);
  const [conversionSubmitting, setConversionSubmitting] = useState(false);
  const [conversionError, setConversionError] = useState("");
  const [conversionSuccess, setConversionSuccess] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [convertedCustomerId, setConvertedCustomerId] = useState(null);
  const [opportunityTitle, setOpportunityTitle] = useState("");
  const [opportunityNotes, setOpportunityNotes] = useState("");
  const [opportunitySubmitting, setOpportunitySubmitting] = useState(false);
  const [createdOrder, setCreatedOrder] = useState(null);
  const [presentationRows, setPresentationRows] = useState([]);
  const [presentationsLoading, setPresentationsLoading] = useState(false);
  const [presentationsLoadedFor, setPresentationsLoadedFor] = useState(null);
  const [presentationsError, setPresentationsError] = useState("");
  const [estimateRows, setEstimateRows] = useState([]);
  const [estimatesLoading, setEstimatesLoading] = useState(false);
  const [estimatesLoadedFor, setEstimatesLoadedFor] = useState(null);
  const [estimatesError, setEstimatesError] = useState("");
  const [salesOrderRows, setSalesOrderRows] = useState([]);
  const [salesOrdersLoading, setSalesOrdersLoading] = useState(false);
  const [salesOrdersLoadedFor, setSalesOrdersLoadedFor] = useState(null);
  const [salesOrdersError, setSalesOrdersError] = useState("");
  const [emailTarget, setEmailTarget] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [nextActivity, setNextActivity] = useState(card?.nextActivity || null);
  const [activityType, setActivityType] = useState(nextActivityPolicyForCard(card).defaultActivityType || DEFAULT_NEXT_ACTIVITY_POLICY.activityType);
  const [activityDueAt, setActivityDueAt] = useState(defaultNextActivityDue(nextActivityPolicyForCard(card)));
  const [activityAssignedToUserId, setActivityAssignedToUserId] = useState("");
  const [activityNotes, setActivityNotes] = useState("");
  const [activitySubmitting, setActivitySubmitting] = useState(false);
  const [activityCompleting, setActivityCompleting] = useState(false);
  const isProspect = card?.cardType === "prospect";
  const conversionStatus = String(card?.meta?.conversionStatus || "").toLowerCase();
  const canReturnProspect = !["converted", "linked"].includes(conversionStatus) && !card?.meta?.linkedCustomerId;
  const canUnclaim = isProspect && isCrmManager && canReturnProspect && card?.assignedToUserId;
  const canManageAssignment = isProspect && isCrmManager && canReturnProspect;
  const openProject = card ? orderPath(instanceUniqueId, card.orderId, "overview") : "#";
  const openProspect = card ? prospectReviewPath(instanceUniqueId, card.entityId) : "#";
  const linkedCustomerId = convertedCustomerId || conversionPreview?.linkedCustomerId || card?.linkedCustomerId || card?.meta?.linkedCustomerId || null;
  const linkedOrderId = card?.linkedOrderId || card?.meta?.linkedOrderId || null;
  const opportunityPendingLabel = linkedCustomerId && !linkedOrderId && ["converted", "linked"].includes(conversionStatus)
    ? conversionStatus === "linked"
      ? "Customer Linked - Opportunity Pending"
      : "Customer Created - Opportunity Pending"
    : "";
  const linkedOrderBusinessId = card?.linkedOrderBusinessId || card?.linkedOrderNumber || card?.meta?.linkedOrderBusinessId || card?.meta?.linkedOrderNumber || null;
  const linkedOrderProjectName = card?.linkedOrderProjectName || card?.meta?.linkedOrderProjectName || null;
  const linkedProjectPath = linkedOrderBusinessId ? orderPath(instanceUniqueId, linkedOrderBusinessId, "overview") : null;
  const prospectTabs = linkedOrderId ? LINKED_PROSPECT_TABS : RAW_PROSPECT_TABS;
  const activeTabLabel = isProspect ? prospectTabs[tab] : WORKSPACE_TABS[tab];

  const loadAssignmentUsers = useCallback(async (search, page, requestId) => {
    assignmentBusy.current = true;
    setAssignmentLoading(true);
    try {
      const result = await fetchInstanceUsers(instanceUniqueId, {
        q: search,
        page,
        limit: 15,
        withCount: true,
        sort: "first-name",
      });
      if (requestId !== assignmentRequest.current) return;
      setAssignmentUsers((previous) => {
        const byId = new Map((page === 1 ? [] : previous).map((user) => [String(user.id), user]));
        result.listOfUsers.forEach((user) => byId.set(String(user.id), user));
        return Array.from(byId.values());
      });
      setAssignmentPage(page);
      setAssignmentTotal(result.totalCount);
    } finally {
      if (requestId === assignmentRequest.current) {
        assignmentBusy.current = false;
        setAssignmentLoading(false);
      }
    }
  }, [instanceUniqueId]);

  useEffect(() => {
    setAssignmentUserId("");
    setAssignmentUser(null);
    setAssignmentInput("");
    setAssignmentSearch("");
    setAssignmentOpen(false);
  }, [card?.cardId, card?.entityId, card?.orderId, instanceUniqueId, currentUserId, initialTab]);

  useEffect(() => {
    const requestId = ++assignmentRequest.current;
    setAssignmentUsers([]);
    setAssignmentPage(0);
    setAssignmentTotal(0);
    if (!assignmentOpen || !canManageAssignment || activeTabLabel !== "Assignment" || !instanceUniqueId) {
      assignmentBusy.current = false;
      setAssignmentLoading(false);
      return undefined;
    }
    assignmentBusy.current = true;
    setAssignmentLoading(true);
    const timer = setTimeout(() => loadAssignmentUsers(assignmentSearch, 1, requestId), 300);
    return () => {
      clearTimeout(timer);
      assignmentRequest.current += 1;
    };
  }, [assignmentOpen, assignmentSearch, canManageAssignment, activeTabLabel, instanceUniqueId, card?.cardId, card?.entityId, loadAssignmentUsers]);

  useEffect(() => {
    if (assignmentPage > 1 && assignmentListbox.current) assignmentListbox.current.scrollTop = assignmentScrollTop.current;
  }, [assignmentUsers, assignmentPage]);
  const openCustomer = linkedCustomerId ? `/instance/${instanceUniqueId}/sales/customers?tab=all&openCustomer=${linkedCustomerId}` : null;
  const openCreatedProject = createdOrder?.OrderId ? orderPath(instanceUniqueId, createdOrder.OrderId, "overview") : null;
  const waitingMessage = card?.soStatus === "Waiting"
    ? card?.subStatus?.label
      ? `The project is currently waiting for more details: ${card.subStatus.label} - Please review the project notes for more information`
      : "The project is currently waiting for more details to proceed, please review the project notes to see why."
    : null;

  useEffect(() => {
    setTab(initialTab || 0);
    setAssignmentUserId("");
    setAssignmentReason("");
    setHistoryRows([]);
    setHistoryLoadedFor(null);
    setHistoryError("");
    setConversionPreview(null);
    setConversionError("");
    setConversionSuccess("");
    setSelectedCustomerId("");
    setConvertedCustomerId(card?.meta?.linkedCustomerId || card?.linkedCustomerId || null);
    setOpportunityTitle(card?.companyName || card?.customerName || card?.projectName || "");
    setOpportunityNotes("");
    setOpportunitySubmitting(false);
    setCreatedOrder(null);
    setPresentationRows([]);
    setPresentationsLoadedFor(null);
    setPresentationsError("");
    setEstimateRows([]);
    setEstimatesLoadedFor(null);
    setEstimatesError("");
    setSalesOrderRows([]);
    setSalesOrdersLoadedFor(null);
    setSalesOrdersError("");
    setEmailTarget(null);
    setNextActivity(card?.nextActivity || null);
    const policy = nextActivityPolicyForCard(card);
    setActivityType(policy.defaultActivityType || DEFAULT_NEXT_ACTIVITY_POLICY.activityType);
    setActivityDueAt(defaultNextActivityDue(policy));
    setActivityAssignedToUserId(String(card?.assignedToUserId || card?.assignedTo?.[0]?.id || currentUserId || ""));
    setActivityNotes("");
    setActivitySubmitting(false);
    setActivityCompleting(false);
  }, [card?.cardId, card?.orderId, currentUserId, initialTab]);

  useEffect(() => {
    if (!instanceUniqueId) return;
    let active = true;
    const headers = { headers: { accessToken: localStorage.getItem("accessToken") } };
    axios.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/byinstanceUniqueId/${instanceUniqueId}`, headers)
      .then((instanceResponse) => {
        const instanceId = instanceResponse?.data?.id;
        if (!instanceId) return null;
        return axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/${instanceId}/email-templates`,
          headers
        );
      })
      .then((templateResponse) => {
        if (!active || !templateResponse) return;
        const templates = templateResponse?.data?.templates || [];
        setEmailTemplates(templates.filter((template) => template.enabled !== false));
      })
      .catch((error) => {
        if (active) console.warn("CRM email templates unavailable:", error?.message);
      });
    return () => {
      active = false;
    };
  }, [instanceUniqueId]);

  useEffect(() => {
    if (activeTabLabel !== "History" || !card || historyLoadedFor === card.cardId) return;
    const cardType = isProspect ? "prospect" : "order";
    const activityId = isProspect ? card.entityId : card.orderId;
    if (!activityId) return;
    let active = true;
    setHistoryLoading(true);
    setHistoryError("");
    retryTransientRequest(() => fetchCrmCardActivity(instanceUniqueId, cardType, activityId), { retries: 1 })
      .then((response) => {
        if (active) {
          const result = response?.data?.events;
          setHistoryRows(Array.isArray(result) ? result : []);
          setHistoryLoadedFor(card.cardId);
        }
      })
      .catch((error) => {
        if (active) {
          if (isInternalApiError(error)) console.warn("CRM card activity load failed:", error);
          const message = getFriendlyApiError(error, "Could not load card activity.");
          setHistoryError(message);
          toast.error(message);
        }
      })
      .finally(() => {
        if (active) setHistoryLoading(false);
      });
    return () => {
      active = false;
    };
  }, [activeTabLabel, card, historyLoadedFor, instanceUniqueId, isProspect]);

  useEffect(() => {
    if (isProspect || tab !== WORKSPACE_TAB_INDEX.Presentation || !card?.orderId || presentationsLoadedFor === card.cardId) return;
    let active = true;
    setPresentationsLoading(true);
    setPresentationsError("");
    retryTransientRequest(() => listPresentationsByOrder(card.orderId), { retries: 1 })
      .then((rows) => {
        if (!active) return;
        setPresentationRows(Array.isArray(rows) ? rows : []);
        setPresentationsLoadedFor(card.cardId);
      })
      .catch((error) => {
        if (active) {
          if (isInternalApiError(error)) console.warn("Presentation history load failed:", error);
          setPresentationsError(getFriendlyApiError(error, "Could not load presentation history."));
        }
      })
      .finally(() => {
        if (active) setPresentationsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [card?.cardId, card?.orderId, isProspect, presentationsLoadedFor, tab]);

  useEffect(() => {
    if (isProspect || tab !== WORKSPACE_TAB_INDEX.Estimate || !card?.orderId || estimatesLoadedFor === card.cardId) return;
    let active = true;
    setEstimatesLoading(true);
    setEstimatesError("");
    retryTransientRequest(() => fetchEstimatesForOrder(card.orderId), { retries: 1 })
      .then((rows) => {
        if (!active) return;
        setEstimateRows(Array.isArray(rows) ? rows : []);
        setEstimatesLoadedFor(card.cardId);
      })
      .catch((error) => {
        if (active) {
          if (isInternalApiError(error)) console.warn("Estimate history load failed:", error);
          setEstimatesError(getFriendlyApiError(error, "Could not load estimate history."));
        }
      })
      .finally(() => {
        if (active) setEstimatesLoading(false);
      });
    return () => {
      active = false;
    };
  }, [card?.cardId, card?.orderId, estimatesLoadedFor, isProspect, tab]);

  useEffect(() => {
    if (isProspect || tab !== WORKSPACE_TAB_INDEX["Sales Order"] || !card?.orderId || salesOrdersLoadedFor === card.cardId) return;
    let active = true;
    setSalesOrdersLoading(true);
    setSalesOrdersError("");
    retryTransientRequest(() => fetchSalesOrdersForOrder(card.orderId), { retries: 1 })
      .then((rows) => {
        if (!active) return;
        setSalesOrderRows(Array.isArray(rows) ? rows : []);
        setSalesOrdersLoadedFor(card.cardId);
      })
      .catch((error) => {
        if (active) {
          if (isInternalApiError(error)) console.warn("Sales order history load failed:", error);
          setSalesOrdersError(getFriendlyApiError(error, "Could not load sales order history."));
        }
      })
      .finally(() => {
        if (active) setSalesOrdersLoading(false);
      });
    return () => {
      active = false;
    };
  }, [card?.cardId, card?.orderId, isProspect, salesOrdersLoadedFor, tab]);

  const refreshDocumentTab = useCallback(async (documentType) => {
    if (!card?.orderId) return;
    if (documentType === "presentation") {
      setPresentationsLoading(true);
      try {
        const rows = await listPresentationsByOrder(card.orderId);
        setPresentationRows(Array.isArray(rows) ? rows : []);
        setPresentationsLoadedFor(card.cardId);
      } finally {
        setPresentationsLoading(false);
      }
      return;
    }
    if (documentType === "estimate") {
      setEstimatesLoading(true);
      try {
        const rows = await fetchEstimatesForOrder(card.orderId);
        setEstimateRows(Array.isArray(rows) ? rows : []);
        setEstimatesLoadedFor(card.cardId);
      } finally {
        setEstimatesLoading(false);
      }
      return;
    }
    setSalesOrdersLoading(true);
    try {
      const rows = await fetchSalesOrdersForOrder(card.orderId);
      setSalesOrderRows(Array.isArray(rows) ? rows : []);
      setSalesOrdersLoadedFor(card.cardId);
    } finally {
      setSalesOrdersLoading(false);
    }
  }, [card?.cardId, card?.orderId]);

  const handleDocumentEmailSend = useCallback(async (value) => {
    if (!emailTarget?.id) return;
    const toEmails = (value?.to || []).map((contact) => contact?.id).filter(Boolean);
    const ccEmails = (value?.cc || []).map((contact) => contact?.id).filter(Boolean);
    const bccEmails = (value?.bcc || []).map((contact) => contact?.id).filter(Boolean);
    if (!toEmails.length) {
      toast.error("Choose at least one recipient in 'To'.");
      return;
    }
    const email = {
      to: toEmails,
      cc: ccEmails,
      bcc: bccEmails,
      subject: value.subject,
      body: value.body,
      templateId: value.templateId || null,
      layout: value.layout || "plain",
    };

    setEmailSending(true);
    try {
      const result = emailTarget.documentType === "presentation"
        ? await sendPresentationEmail(emailTarget.id, instanceUniqueId, email)
        : await sendEstimationEmail(emailTarget.id, instanceUniqueId, email);
      setEmailTarget(null);
      setHistoryLoadedFor(null);
      toast.success(result?.simulated ? result.message || "Email simulated." : result?.message || "Email sent.");
      const refreshResults = await Promise.allSettled([
        refreshDocumentTab(emailTarget.documentType),
        onRefreshBoard?.(),
      ]);
      if (refreshResults.some((entry) => entry.status === "rejected")) {
        toast.error("Email succeeded, but some CRM data could not be refreshed.");
      }
    } catch (error) {
      const delivered = error?.response?.data?.delivered === true;
      toast.error(delivered
        ? "Email was accepted, but the workflow status could not be saved. Refresh before retrying."
        : getFriendlyApiError(error, "Could not send email."));
    } finally {
      setEmailSending(false);
    }
  }, [emailTarget, instanceUniqueId, onRefreshBoard, refreshDocumentTab]);

  const openDocumentEmailDialog = useCallback((target, options = {}) => {
    if (options.resend) {
      const label = target.documentType === "agreement"
        ? "agreement"
        : target.documentType === "estimate"
          ? "estimate"
          : "presentation";
      const confirmed = window.confirm(
        `This ${label} was already sent to the customer. Are you sure you want to resend the same ${label}?`
      );
      if (!confirmed) return;
    }
    setEmailTarget({ ...target, resend: options.resend === true });
  }, []);

  const submitAssignment = () => {
    if (!assignmentUserId) {
      toast.error("Select a salesperson.");
      return;
    }
    if (Number(assignmentUserId) === Number(card?.assignedToUserId)) {
      toast.info("This prospect is already assigned to that salesperson.");
      return;
    }
    const payload = { toUserId: Number(assignmentUserId), reason: assignmentReason || undefined };
    if (card?.assignedToUserId) {
      onReassignProspect?.(card, { ...payload, fromUserId: card.assignedToUserId });
    } else {
      onAssignProspect?.(card, payload);
    }
  };

  const loadConversionPreview = useCallback(async () => {
    if (!card?.entityId) return;
    setConversionLoading(true);
    setConversionError("");
    try {
      const response = await retryTransientRequest(
        () => getMarketingProspectConversionPreview(instanceUniqueId, card.entityId),
        { retries: 1 }
      );
      const result = response?.data?.result || null;
      setConversionPreview(result);
      setConvertedCustomerId(result?.linkedCustomerId || null);
      const strongCandidate = (result?.duplicateCandidates || []).find((candidate) => Number(candidate.score) >= 90);
      setSelectedCustomerId(strongCandidate?.id ? String(strongCandidate.id) : "");
    } catch (error) {
      setConversionPreview(null);
      if (isInternalApiError(error)) console.warn("Conversion preview load failed:", error);
      setConversionError(getFriendlyApiError(error, "Could not load conversion preview."));
    } finally {
      setConversionLoading(false);
    }
  }, [card?.entityId, instanceUniqueId]);

  const handleConvertAction = useCallback(async (action) => {
    if (!card?.entityId) return;
    const needsCustomer = action === "link" || action === "update";
    if (needsCustomer && !selectedCustomerId) {
      setConversionError("Select a duplicate customer before linking or updating.");
      return;
    }
    const strongDuplicate = (conversionPreview?.duplicateCandidates || []).some((candidate) => Number(candidate.score) >= 90);
    const confirmDuplicateOverride = action === "create" && strongDuplicate
      ? window.confirm("A strong duplicate match exists. Create a new Customer anyway?")
      : false;
    if (action === "create" && strongDuplicate && !confirmDuplicateOverride) return;

    setConversionSubmitting(true);
    setConversionError("");
    setConversionSuccess("");
    try {
      const response = await convertMarketingProspect(instanceUniqueId, card.entityId, {
        action,
        customerId: needsCustomer ? Number(selectedCustomerId) : undefined,
        confirmDuplicateOverride,
      });
      const result = response?.data?.result || {};
      setConvertedCustomerId(result.customerId || null);
      setConversionSuccess(result.noMaterialChanges ? "Prospect is already linked with no material changes." : "Customer conversion details were applied.");
      await loadConversionPreview();
      await onRefreshBoard?.();
    } catch (error) {
      const details = error?.response?.data?.details;
      if (Array.isArray(details?.duplicateCandidates) && details.duplicateCandidates.length) {
        setConversionPreview((current) => ({
          ...(current || {}),
          duplicateCandidates: details.duplicateCandidates,
        }));
      }
      if (isInternalApiError(error)) console.warn("Prospect conversion failed:", error);
      setConversionError(getFriendlyApiError(error, "Could not convert prospect."));
    } finally {
      setConversionSubmitting(false);
    }
  }, [card?.entityId, conversionPreview, instanceUniqueId, loadConversionPreview, onRefreshBoard, selectedCustomerId]);

  const handleCreateOpportunity = useCallback(async () => {
    if (!card?.entityId || !linkedCustomerId) {
      toast.error("Link this prospect to a Customer before creating an opportunity.");
      return;
    }
    if (!window.confirm("Create an Opportunity project for this linked Customer?")) return;

    setOpportunitySubmitting(true);
    setConversionError("");
    try {
      const response = await createProspectOpportunity(instanceUniqueId, card.entityId, {
        customerId: Number(linkedCustomerId),
        title: opportunityTitle || card.projectName,
        assignedToUserId: card.assignedToUserId || currentUserId,
        notes: opportunityNotes || undefined,
      });
      setCreatedOrder(response?.data?.order || null);
      toast.success(response?.data?.message || "Opportunity project ready.");
      await onRefreshBoard?.();
    } catch (error) {
      if (isInternalApiError(error)) console.warn("Create opportunity failed:", error);
      const message = getFriendlyApiError(error, "Could not create opportunity.");
      setConversionError(message);
      toast.error(message);
    } finally {
      setOpportunitySubmitting(false);
    }
  }, [card, currentUserId, instanceUniqueId, linkedCustomerId, onRefreshBoard, opportunityNotes, opportunityTitle]);

  const handleCreateNextActivity = useCallback(async () => {
    if (!card) return;
    if (!activityDueAt) {
      toast.error("Choose a due date/time.");
      return;
    }
    const cardType = isProspect ? "prospect" : "order";
    const routeId = activityRouteIdForCard(card);
    if (!routeId) {
      toast.error("This card cannot be linked to an activity yet.");
      return;
    }
    setActivitySubmitting(true);
    try {
      const result = await createCrmCardActivity(instanceUniqueId, cardType, routeId, {
        activityType,
        title: `${titleize(activityType)}: ${card.projectName || card.customerName || "CRM card"}`,
        dueAt: new Date(activityDueAt).toISOString(),
        assignedToUserId: activityAssignedToUserId ? Number(activityAssignedToUserId) : undefined,
        notes: activityNotes || undefined,
      });
      setNextActivity(result.nextActivity || result.activity || null);
      setHistoryLoadedFor(null);
      setActivityNotes("");
      setActivityDueAt(defaultNextActivityDue(nextActivityPolicyForCard(card)));
      toast.success("Next activity created.");
      await onRefreshBoard?.();
    } catch (error) {
      if (isInternalApiError(error)) console.warn("Create CRM activity failed:", error);
      toast.error(getFriendlyApiError(error, "Could not create next activity."));
    } finally {
      setActivitySubmitting(false);
    }
  }, [activityAssignedToUserId, activityDueAt, activityNotes, activityType, card, instanceUniqueId, isProspect, onRefreshBoard]);

  const handleCompleteNextActivity = useCallback(async () => {
    if (!nextActivity?.id) return;
    setActivityCompleting(true);
    try {
      const result = await completeCrmActivity(instanceUniqueId, nextActivity.id);
      setNextActivity(result.nextActivity || null);
      setHistoryLoadedFor(null);
      toast.success("Next activity completed.");
      await onRefreshBoard?.();
    } catch (error) {
      if (isInternalApiError(error)) console.warn("Complete CRM activity failed:", error);
      toast.error(getFriendlyApiError(error, "Could not complete next activity."));
    } finally {
      setActivityCompleting(false);
    }
  }, [instanceUniqueId, nextActivity?.id, onRefreshBoard]);

  return (
    <Dialog
      open={!!card}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: "calc(100vw - 32px)", sm: "min(92vw, 1100px)" },
          height: { xs: "calc(100dvh - 32px)", sm: "min(88vh, 760px)" },
          maxHeight: { xs: "calc(100dvh - 32px)", sm: "min(88vh, 760px)" },
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
          overflow: "hidden",
          boxShadow: "0 24px 70px rgba(15, 23, 42, 0.32)",
        },
      }}
    >
      {card ? (
        <>
          <DialogTitle sx={{ p: 0, flexShrink: 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2} sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 900 }}>
                    {isProspect ? `${card.source || "insightly"} ${card.meta?.externalType || "prospect"}` : `#${card.projectNumber || card.orderId}`}
                  </Typography>
                  <Box component="span" sx={{ ...pillSx, color: (COLUMN_META[card.pipelineColumn] || COLUMN_META.opportunity).tone, backgroundColor: (COLUMN_META[card.pipelineColumn] || COLUMN_META.opportunity).bg }}>
                    {card.statusChip || card.soStatus || "Opportunity"}
                  </Box>
                </Stack>
                <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15, ...truncate }}>
                  {card.projectName || "Untitled project"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={truncate}>
                  {card.customerName || "No customer"}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button component={Link} to={isProspect ? openProspect : openProject} {...newTabLinkProps} size="small" variant="outlined" endIcon={<OpenInNewRoundedIcon />}>
                  {isProspect ? "Open Prospects" : "Open Project"}
                </Button>
                <IconButton onClick={onClose} aria-label="Close pipeline workspace"><CloseRoundedIcon /></IconButton>
              </Stack>
            </Stack>
          </DialogTitle>
          <Tabs
            value={tab}
            onChange={(_, next) => setTab(next)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              width: "100%",
              minWidth: 0,
              maxWidth: "100%",
              flexShrink: 0,
              overflow: "hidden",
              borderBottom: "1px solid",
              borderColor: "divider",
              "& .MuiTabs-scroller": {
                minWidth: 0,
              },
              "& .MuiTab-root": {
                minWidth: { xs: 88, sm: 96 },
                flexShrink: 0,
                whiteSpace: "nowrap",
              },
            }}
          >
            {(isProspect ? prospectTabs : WORKSPACE_TABS).map((label) => <Tab key={label} label={label} />)}
          </Tabs>
          <DialogContent sx={{ p: 2, flex: 1, minHeight: 0, overflowY: "auto", ...scrollbarSx }}>
            {isProspect && activeTabLabel === "Overview" ? (
              <Stack spacing={2}>
                <Alert severity="info">
                  Review source details and duplicates before converting or creating a project. Managers assign leads from All Leads; assignment does not create a Customer or Order.
                </Alert>
                <AccountabilitySummary card={card} />
                <TagEditor card={card} tags={availableTags} instanceUniqueId={instanceUniqueId} onRefreshBoard={onRefreshBoard} />
                <InfoGrid rows={[
                  ["Prospect #", card.entityId],
                  ["Title", card.projectName],
                  ["Company", card.companyName || card.customerName],
                  ["Contact", card.contactName],
                  ["Email", card.email],
                  ["Phone", card.phone],
                  ["Assignment", card.assignedToName || "Unassigned"],
                  ["Status", card.statusChip || card.status],
                  ["Stage age", card.stageAgeDays == null ? "n/a" : `${card.stageAgeDays} days`],
                  ["Updated", shortDate(card.meta?.updatedAt)],
                ]} />
                <NextActivityPanel
                  nextActivity={nextActivity}
                  activityType={activityType}
                  setActivityType={setActivityType}
                  activityDueAt={activityDueAt}
                  setActivityDueAt={setActivityDueAt}
                  activityAssignedToUserId={activityAssignedToUserId}
                  setActivityAssignedToUserId={setActivityAssignedToUserId}
                  activityNotes={activityNotes}
                  setActivityNotes={setActivityNotes}
                  users={users}
                  onCreate={handleCreateNextActivity}
                  onComplete={handleCompleteNextActivity}
                  submitting={activitySubmitting}
                  completing={activityCompleting}
                />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {canUnclaim ? <Button variant="outlined" color="warning" onClick={() => onUnclaimProspect?.(card)}>Unassign</Button> : null}
                  <Button component={Link} to={openProspect} {...newTabLinkProps} variant="outlined" endIcon={<OpenInNewRoundedIcon />}>Open Prospects Review</Button>
                </Stack>
              </Stack>
            ) : null}
            {isProspect && activeTabLabel === "Source" ? (
              <InfoGrid rows={[
                ["Source", card.source || "insightly"],
                ["External type", card.meta?.externalType],
                ["External ID", card.meta?.externalId],
                ["Owner", card.meta?.ownerName],
                ["Source status", card.meta?.sourceStatus],
                ["Stage", card.meta?.stageKey],
                ["Conversion status", card.meta?.conversionStatus],
              ]} />
            ) : null}
            {isProspect && activeTabLabel === "Assignment" ? (
              <Stack spacing={2}>
                <Alert severity="info">Assignment can be reversed while the prospect is not converted or linked.</Alert>
                <InfoGrid rows={[
                  ["Assigned user", card.assignedToName || userNameById(users, card.assignedToUserId) || "Unassigned"],
                  ["Assigned user ID", card.assignedToUserId],
                  ["Claimed at", shortDate(card.claimedAt)],
                  ["Lane", card.pipelineColumnLabel],
                ]} />
                {canManageAssignment ? (
                  <Stack spacing={1.25}>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                      <Autocomplete
                        size="small"
                        open={assignmentOpen}
                        onOpen={() => setAssignmentOpen(true)}
                        onClose={() => setAssignmentOpen(false)}
                        options={assignmentUser && !assignmentUsers.some((user) => String(user.id) === String(assignmentUser.id)) ? [...assignmentUsers, assignmentUser] : assignmentUsers}
                        value={assignmentUser}
                        inputValue={assignmentInput}
                        loading={assignmentLoading}
                        filterOptions={() => assignmentUsers}
                        getOptionLabel={userName}
                        isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                        onInputChange={(_, value, reason) => {
                          setAssignmentInput(value);
                          if ((reason === "input" || reason === "clear") && value !== assignmentSearch) {
                            assignmentRequest.current += 1;
                            setAssignmentSearch(value);
                          }
                        }}
                        onChange={(_, user) => {
                          setAssignmentUser(user);
                          setAssignmentUserId(user ? String(user.id) : "");
                        }}
                        ListboxProps={{
                          ref: assignmentListbox,
                          style: { maxHeight: 320, overflowY: "auto" },
                          onScroll: (event) => {
                            const list = event.currentTarget;
                            if (!assignmentBusy.current && assignmentPage > 0 && assignmentUsers.length < assignmentTotal && assignmentPage * 15 < assignmentTotal &&
                                list.scrollHeight - list.scrollTop - list.clientHeight < 64) {
                              assignmentScrollTop.current = list.scrollTop;
                              loadAssignmentUsers(assignmentSearch, assignmentPage + 1, assignmentRequest.current);
                            }
                          },
                        }}
                        renderOption={(props, user) => {
                          const { key, ...optionProps } = props;
                          return <li {...optionProps} key={user.id}>{userName(user)}{user.role ? ` - ${user.role}` : ""}</li>;
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={card.assignedToUserId ? "Reassign to salesperson" : "Assign to salesperson"}
                            inputProps={{ ...params.inputProps, "aria-label": card.assignedToUserId ? "Reassign to salesperson" : "Assign to salesperson" }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: <>{assignmentLoading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>,
                            }}
                          />
                        )}
                        sx={{ minWidth: 240, "& .MuiInputBase-root": { fontSize: 14 } }}
                      />
                      <TextField
                        size="small"
                        label="Reason"
                        value={assignmentReason}
                        onChange={(event) => setAssignmentReason(event.target.value)}
                        sx={{ minWidth: 220, flex: 1 }}
                      />
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button variant="contained" onClick={submitAssignment}>
                        {card.assignedToUserId ? "Reassign" : "Assign"}
                      </Button>
                      {canUnclaim ? <Button variant="outlined" color="warning" onClick={() => onUnclaimProspect?.(card)}>Unassign</Button> : null}
                    </Stack>
                  </Stack>
                ) : null}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {canUnclaim && !canManageAssignment ? <Button variant="outlined" color="warning" onClick={() => onUnclaimProspect?.(card)}>Unassign</Button> : null}
                </Stack>
              </Stack>
            ) : null}
            {isProspect && activeTabLabel === "Convert" ? (
              <Stack spacing={2}>
                <Alert severity="info">
                  Convert/link only after reviewing duplicate matches. This does not create an Order or Project.
                </Alert>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip size="small" label={`Status: ${titleize(conversionPreview?.conversionStatus || card.meta?.conversionStatus || "not_converted")}`} />
                  {linkedCustomerId ? <Chip size="small" color="success" label={`Customer #${linkedCustomerId}`} /> : null}
                  {opportunityPendingLabel ? <Chip size="small" color="warning" variant="outlined" label={opportunityPendingLabel} /> : null}
                  {conversionPreview?.recommendedAction ? <Chip size="small" variant="outlined" label={`Recommended: ${titleize(conversionPreview.recommendedAction)}`} /> : null}
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button variant="contained" onClick={loadConversionPreview} disabled={conversionLoading || conversionSubmitting}>
                    {conversionPreview ? "Refresh Preview" : "Check Duplicates"}
                  </Button>
                  {openCustomer ? <Button component={Link} to={openCustomer} {...newTabLinkProps} variant="outlined" endIcon={<OpenInNewRoundedIcon />}>Open Customer</Button> : null}
                  <Button component={Link} to={openProspect} {...newTabLinkProps} variant="outlined" endIcon={<OpenInNewRoundedIcon />}>Open Full Review</Button>
                </Stack>
                {conversionLoading ? <CircularProgress size={22} /> : null}
                {conversionError ? <Alert severity="error">{conversionError}</Alert> : null}
                {conversionSuccess ? <Alert severity="success">{conversionSuccess}</Alert> : null}
                {conversionPreview ? (
                  <Stack spacing={1.5}>
                    {!conversionPreview.conversionSupported ? (
                      <Alert severity="warning">{conversionPreview.unsupportedReason || "This prospect type is not supported for customer conversion."}</Alert>
                    ) : null}
                    {conversionPreview.linkedCustomerSummary ? (
                      <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.25 }}>
                        <Typography variant="caption" color="text.secondary">Linked Customer</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 850 }}>
                          {conversionPreview.linkedCustomerSummary.businessName || conversionPreview.linkedCustomerSummary.emailAddress || `Customer #${conversionPreview.linkedCustomerSummary.id}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          #{conversionPreview.linkedCustomerSummary.id} - {conversionPreview.linkedCustomerSummary.emailAddress || "No email"} - {conversionPreview.linkedCustomerSummary.phoneNumber || "No phone"}
                        </Typography>
                      </Box>
                    ) : null}
                    <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.25, backgroundColor: "#fff" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>Proposed customer details</Typography>
                      <InfoGrid rows={previewFieldEntries(conversionPreview.normalizedPayload).map(([key, value]) => [
                        titleize(key),
                        typeof value === "object" ? JSON.stringify(value) : value,
                      ])} />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>Duplicate candidates</Typography>
                      {conversionPreview.duplicateCandidates?.length ? (
                        <Stack spacing={1}>
                          {conversionPreview.duplicateCandidates.slice(0, 5).map((candidate) => {
                            const selected = String(candidate.id) === String(selectedCustomerId);
                            return (
                              <Box
                                key={candidate.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => setSelectedCustomerId(String(candidate.id))}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter" || event.key === " ") setSelectedCustomerId(String(candidate.id));
                                }}
                                sx={{
                                  border: "1px solid",
                                  borderColor: selected ? "primary.main" : "divider",
                                  borderRadius: 1.5,
                                  p: 1.25,
                                  cursor: "pointer",
                                  backgroundColor: selected ? "rgba(37, 99, 235, 0.08)" : "#fff",
                                }}
                              >
                                <Stack direction="row" justifyContent="space-between" gap={1}>
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 850 }}>
                                      {candidate.businessName || candidate.emailAddress || `Customer #${candidate.id}`}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      #{candidate.id} - {candidate.emailAddress || "No email"} - {candidate.phoneNumber || "No phone"}
                                    </Typography>
                                  </Box>
                                  <Chip size="small" label={candidate.score ?? "n/a"} color={Number(candidate.score) >= 90 ? "success" : Number(candidate.score) >= 70 ? "warning" : "default"} />
                                </Stack>
                                {candidate.reasons?.length ? (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                                    {candidate.reasons.join(" ")}
                                  </Typography>
                                ) : null}
                              </Box>
                            );
                          })}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">No duplicate candidates found.</Typography>
                      )}
                    </Box>
                    {conversionPreview.conflicts?.length ? (
                      <Alert severity="warning">
                        {conversionPreview.conflicts.map((conflict) => (
                          <Typography key={conflict.field} variant="body2">
                            {conflict.field}: existing "{String(conflict.currentValue)}", incoming "{String(conflict.incomingValue)}"
                          </Typography>
                        ))}
                      </Alert>
                    ) : null}
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button
                        variant="contained"
                        disabled={Boolean(linkedCustomerId) || conversionSubmitting || !conversionPreview.conversionSupported || conversionPreview.noMaterialChanges}
                        onClick={() => handleConvertAction("create")}
                      >
                        Create New Customer
                      </Button>
                      <Button
                        variant="outlined"
                        disabled={Boolean(linkedCustomerId) || conversionSubmitting || !conversionPreview.conversionSupported || conversionPreview.noMaterialChanges || !selectedCustomerId}
                        onClick={() => handleConvertAction("link")}
                      >
                        Link Selected Customer
                      </Button>
                      <Button
                        variant="outlined"
                        disabled={conversionSubmitting || !conversionPreview.conversionSupported || conversionPreview.noMaterialChanges || !selectedCustomerId}
                        onClick={() => handleConvertAction("update")}
                      >
                        Update Selected Customer
                      </Button>
                    </Stack>
                    <Box sx={{ border: "1px solid", borderColor: linkedCustomerId ? "divider" : "rgba(148, 163, 184, 0.45)", borderRadius: 1.5, p: 1.5, backgroundColor: linkedCustomerId ? "#fff" : "#f8fafc" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.5 }}>Create Opportunity / Project</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                        Creates a project in Opportunity with SO status Quote Requested. No presentation, estimate, or sales order document is created.
                      </Typography>
                      {!linkedCustomerId ? (
                        <Alert severity="info">Link or create a Customer before creating an opportunity.</Alert>
                      ) : (
                        <Stack spacing={1.25}>
                          <TextField
                            size="small"
                            label="Project title"
                            value={opportunityTitle}
                            onChange={(event) => setOpportunityTitle(event.target.value)}
                            fullWidth
                          />
                          <TextField
                            size="small"
                            label="Notes"
                            value={opportunityNotes}
                            onChange={(event) => setOpportunityNotes(event.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                          />
                          <Typography variant="caption" color="text.secondary">
                            Assigned to {card.assignedToName || userNameById(users, card.assignedToUserId || currentUserId)}
                          </Typography>
                          {linkedOrderId && !createdOrder ? (
                            <Alert severity="info">An opportunity already exists for this prospect. Load it to open the project.</Alert>
                          ) : null}
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {!createdOrder ? (
                              <Button variant={linkedOrderId ? "outlined" : "contained"} onClick={handleCreateOpportunity} disabled={opportunitySubmitting}>
                                {opportunitySubmitting ? "Loading..." : linkedOrderId ? "Load Existing Project" : "Create Opportunity / Project"}
                              </Button>
                            ) : null}
                            {openCreatedProject ? <Button component={Link} to={openCreatedProject} {...newTabLinkProps} variant="outlined" endIcon={<OpenInNewRoundedIcon />}>Open Project</Button> : null}
                          </Stack>
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                ) : (
                  <Alert severity="info">Run duplicate check before creating or linking a Customer.</Alert>
                )}
              </Stack>
            ) : null}
            {isProspect && activeTabLabel === "Project" ? (
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 900 }}>Linked Project</Typography>
                <InfoGrid rows={[
                  ["Project #", linkedOrderBusinessId || "Unavailable"],
                  ["Project name", linkedOrderProjectName],
                  ["Linked order record", linkedOrderId],
                ]} />
                {linkedProjectPath ? (
                  <Button component={Link} to={linkedProjectPath} {...newTabLinkProps} variant="contained" endIcon={<OpenInNewRoundedIcon />} sx={{ alignSelf: "flex-start" }}>
                    Open Project / Cart
                  </Button>
                ) : (
                  <Alert severity="warning">Linked project exists but route id is unavailable.</Alert>
                )}
              </Stack>
            ) : null}
            {isProspect && activeTabLabel === "History" ? (
              <CardActivityPanel rows={historyRows} loading={historyLoading} error={historyError} />
            ) : null}
            {isProspect && activeTabLabel === "Communication" ? (
              <CommunicationPanel card={card} instanceUniqueId={instanceUniqueId} templates={emailTemplates} />
            ) : null}
            {!isProspect && tab === 0 ? (
              <Stack spacing={2}>
                {waitingMessage ? <Alert severity="info">{waitingMessage}</Alert> : null}
                <AccountabilitySummary card={card} />
                <TagEditor card={card} tags={availableTags} instanceUniqueId={instanceUniqueId} onRefreshBoard={onRefreshBoard} />
                <InfoGrid rows={[
                  ["Project #", card.projectNumber],
                  ["Project name", card.projectName],
                  ["Customer", card.customerName],
                  ["Email", card.email],
                  ["Phone", card.phone],
                  ["SO status", card.soStatus],
                  ["PO status", card.orderStatus?.PO || "n/a"],
                  ["In-hands", shortDate(card.inHandsDate)],
                  ["Assigned", assignedNames(card.assignedTo).join(", ") || "None"],
                  ["Stage age", card.stageAgeDays == null ? "n/a" : `${card.stageAgeDays} days`],
                  ["Estimate aging", card.estimateAgeBucket && card.estimateAgeBucket !== "none" ? card.estimateAgeBucket : "No estimate"],
                  ["Updated", shortDate(card.updatedAt)],
                ]} />
                <NextActivityPanel
                  nextActivity={nextActivity}
                  activityType={activityType}
                  setActivityType={setActivityType}
                  activityDueAt={activityDueAt}
                  setActivityDueAt={setActivityDueAt}
                  activityAssignedToUserId={activityAssignedToUserId}
                  setActivityAssignedToUserId={setActivityAssignedToUserId}
                  activityNotes={activityNotes}
                  setActivityNotes={setActivityNotes}
                  users={users}
                  onCreate={handleCreateNextActivity}
                  onComplete={handleCompleteNextActivity}
                  submitting={activitySubmitting}
                  completing={activityCompleting}
                />
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button component={Link} to={openProject} {...newTabLinkProps} variant="contained" endIcon={<OpenInNewRoundedIcon />}>Open Full Project</Button>
                  {openCustomer ? <Button component={Link} to={openCustomer} {...newTabLinkProps} variant="outlined" endIcon={<OpenInNewRoundedIcon />}>Open Customer</Button> : null}
                </Stack>
              </Stack>
            ) : null}
            {!isProspect && tab === 1 ? (
              <OrderCartPanel
                orderId={cartOrderIdForCard(card)}
                overviewPath={openProject}
                instanceUniqueId={instanceUniqueId}
              />
            ) : null}
            {!isProspect && tab === 2 ? (
              <PresentationHistoryPanel
                instanceUniqueId={instanceUniqueId}
                orderId={card.orderId}
                rows={presentationRows}
                loading={presentationsLoading}
                error={presentationsError}
                projectPresentationPath={orderPath(instanceUniqueId, card.orderId, "presentation")}
                onSend={(presentation, options) => openDocumentEmailDialog({
                  documentType: "presentation",
                  id: presentation.presentationId || presentation.id,
                  customerEmail: presentation.clientContactEmail || card.email,
                }, options)}
              />
            ) : null}
            {!isProspect && tab === 3 ? (
              <EstimateHistoryPanel
                instanceUniqueId={instanceUniqueId}
                orderId={card.orderId}
                rows={estimateRows}
                loading={estimatesLoading}
                error={estimatesError}
                projectEstimatePath={orderPath(instanceUniqueId, card.orderId, "estimation")}
                onSend={(estimate, options) => openDocumentEmailDialog({
                  documentType: "estimate",
                  id: estimate.estimationId || estimate.id,
                  customerEmail: estimate.clientEmail || card.email,
                }, options)}
              />
            ) : null}
            {!isProspect && tab === 4 ? (
              <SalesOrderHistoryPanel
                instanceUniqueId={instanceUniqueId}
                orderId={card.orderId}
                rows={salesOrderRows}
                loading={salesOrdersLoading}
                error={salesOrdersError}
                projectSalesOrderPath={orderPath(instanceUniqueId, card.orderId, "sales-order")}
                onSend={(salesOrder, options) => openDocumentEmailDialog({
                  documentType: "agreement",
                  id: salesOrder.estimationId || salesOrder.id,
                  customerEmail: salesOrder.clientEmail || card.email,
                }, options)}
              />
            ) : null}
            {!isProspect && tab === 5 ? (
              <Stack spacing={2}>
                <Alert severity="info">
                  {card.pipelineColumn === "loss"
                    ? "LOSS cards cannot be dragged back to the active pipeline. Use Revive to return this lead to Opportunity with a recorded reason."
                    : "WIN is read-only here. Post-sale lifecycle stays controlled by existing PO, invoice, payment, and project workflows."}
                </Alert>
                <InfoGrid rows={[
                  ["Current SO status", card.soStatus],
                  ["Pipeline column", card.pipelineColumnLabel],
                  ["Purchase orders", card.purchaseOrderSummary?.count || 0],
                  ["Invoices", card.invoiceSummary ? card.invoiceSummary.count || 0 : "Open project"],
                  ["Paid invoices", card.invoiceSummary ? card.invoiceSummary.paidCount || 0 : "Open project"],
                ]} />
                {card.pipelineColumn === "loss" ? (
                  <Box>
                    <Button variant="contained" color="warning" onClick={() => onReviveLoss?.(card)}>
                      Revive
                    </Button>
                  </Box>
                ) : null}
              </Stack>
            ) : null}
            {!isProspect && tab === WORKSPACE_TAB_INDEX.History ? (
              <CardActivityPanel rows={historyRows} loading={historyLoading} error={historyError} />
            ) : null}
            {!isProspect && tab === WORKSPACE_TAB_INDEX.Communication ? (
              <CommunicationPanel card={card} instanceUniqueId={instanceUniqueId} templates={emailTemplates} />
            ) : null}
          </DialogContent>
          <DocumentEmailDialog
            open={Boolean(emailTarget)}
            onClose={() => setEmailTarget(null)}
            onSend={handleDocumentEmailSend}
            sending={emailSending}
            documentType={emailTarget?.documentType}
            documentId={emailTarget?.id}
            customerEmail={emailTarget?.customerEmail}
            assignedTo={card.assignedTo}
            users={users}
            currentUserId={currentUserId}
            templates={emailTemplates}
            resend={emailTarget?.resend === true}
          />
        </>
      ) : null}
    </Dialog>
  );
}

function NextActivityPanel({
  nextActivity,
  activityType,
  setActivityType,
  activityDueAt,
  setActivityDueAt,
  activityAssignedToUserId,
  setActivityAssignedToUserId,
  activityNotes,
  setActivityNotes,
  users = [],
  onCreate,
  onComplete,
  submitting = false,
  completing = false,
}) {
  const isOverdue = nextActivity?.dueAt && new Date(nextActivity.dueAt).getTime() < Date.now();
  return (
    <Box sx={{ border: "1px solid", borderColor: isOverdue ? "error.light" : "divider", borderRadius: 1.5, p: 1.5, backgroundColor: isOverdue ? "#fef2f2" : "#fff" }}>
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>Next Activity</Typography>
          {nextActivity ? (
            <Chip size="small" color={isOverdue ? "error" : "primary"} variant={isOverdue ? "filled" : "outlined"} label={isOverdue ? "Overdue" : "Open"} />
          ) : (
            <Chip size="small" color="warning" variant="outlined" label="No next activity" />
          )}
        </Stack>

        {nextActivity ? (
          <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1, backgroundColor: "#f8fafc" }}>
            <Stack spacing={0.75}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" sx={{ fontWeight: 850 }}>{nextActivity.title || titleize(nextActivity.activityType)}</Typography>
                <Chip size="small" label={titleize(nextActivity.activityType)} />
              </Stack>
              <InfoGrid rows={[
                ["Due", dateTimeLabel(nextActivity.dueAt)],
                ["Assigned", nextActivity.assignedToName || userNameById(users, nextActivity.assignedToUserId)],
              ]} />
              {nextActivity.notes ? <Typography variant="body2" color="text.secondary">{nextActivity.notes}</Typography> : null}
              <Button variant="contained" size="small" onClick={onComplete} disabled={completing} sx={{ alignSelf: "flex-start" }}>
                {completing ? "Completing..." : "Complete"}
              </Button>
            </Stack>
          </Box>
        ) : null}

        <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
          <TextField
            select
            size="small"
            label="Type"
            value={activityType}
            SelectProps={{ native: true }}
            onChange={(event) => setActivityType(event.target.value)}
            sx={{ minWidth: 150 }}
          >
            <option value="follow_up">Follow-up</option>
            <option value="call">Call</option>
            <option value="email">Email</option>
            <option value="todo">Todo</option>
          </TextField>
          <TextField
            size="small"
            label="Due"
            type="datetime-local"
            value={activityDueAt}
            onChange={(event) => setActivityDueAt(event.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 210 }}
          />
          <TextField
            select
            size="small"
            label="Assigned"
            value={activityAssignedToUserId}
            SelectProps={{ native: true }}
            onChange={(event) => setActivityAssignedToUserId(event.target.value)}
            sx={{ minWidth: 210 }}
          >
            <option value="">Current user</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {userName(user)}{user.role ? ` - ${user.role}` : ""}
              </option>
            ))}
          </TextField>
        </Stack>
        <TextField
          size="small"
          label="Notes"
          value={activityNotes}
          onChange={(event) => setActivityNotes(event.target.value)}
          fullWidth
          multiline
          minRows={2}
        />
        <Button variant="outlined" onClick={onCreate} disabled={submitting} sx={{ alignSelf: "flex-start" }}>
          {submitting ? "Creating..." : nextActivity ? "Add Another Activity" : "Create Next Activity"}
        </Button>
      </Stack>
    </Box>
  );
}

function InfoGrid({ rows }) {
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
      {rows.map(([label, value]) => (
        <Box key={label}>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          <Typography variant="body2" sx={{ fontWeight: 650 }}>{value || "n/a"}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function activityTimestamp(value) {
  if (!value) return "n/a";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "n/a" : date.toLocaleString();
}

const activityActorLabel = (row) => row?.actorName || (row?.actorUserId ? `User #${row.actorUserId}` : "Unknown");

const activityChangeLabel = (row) => {
  const metadata = row?.metadata || {};
  const fromPerson = metadata.fromUserName || (metadata.fromUserId ? `User #${metadata.fromUserId}` : "");
  const toPerson = metadata.toUserName || (metadata.toUserId ? `User #${metadata.toUserId}` : "");
  if (fromPerson || toPerson) return `From: ${fromPerson || "Unassigned"} \u2192 To: ${toPerson || "Unassigned"}`;

  const fromStatus = metadata.fromStatus || metadata.from;
  const toStatus = metadata.toStatus || metadata.to || metadata.SO;
  if (fromStatus || toStatus) return `Stage: ${fromStatus || "Unknown"} \u2192 ${toStatus || "Unknown"}`;

  if (metadata.customerId || metadata.linkedCustomerId) return `Customer: #${metadata.customerId || metadata.linkedCustomerId}`;
  if (metadata.orderId) return `Project: #${metadata.orderId}`;
  if (metadata.presentationId) return `Presentation: #${metadata.presentationId}`;
  return "";
};

function CardActivityPanel({ rows = [], loading = false, error = "" }) {
  return (
    <Stack spacing={1.25}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="h6" sx={{ fontWeight: 900 }}>Activity</Typography>
        <Chip size="small" label={rows.length} />
      </Stack>
      {loading ? <CircularProgress size={22} /> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
      {!loading && !error && !rows.length ? (
        <Alert severity="info">No activity has been recorded yet.</Alert>
      ) : null}
      {rows.map((row, index) => {
        const changeLabel = activityChangeLabel(row);
        return (
          <Box
            key={`${row.type || "activity"}-${row.timestamp || index}-${index}`}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              p: 1.25,
              backgroundColor: "#fff",
            }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" sx={{ fontWeight: 900 }}>
                  {row.label || titleize(row.type)}
                </Typography>
                {row.source ? <Chip size="small" variant="outlined" label={titleize(row.source)} /> : null}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {activityTimestamp(row.timestamp)}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              By: {activityActorLabel(row)}
            </Typography>
            {row.description ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.65 }}>
                {row.description}
              </Typography>
            ) : null}
            {changeLabel ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.6, fontWeight: 750 }}>
                {changeLabel}
              </Typography>
            ) : null}
          </Box>
        );
      })}
    </Stack>
  );
}

function communicationDateInputValue() {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function communicationBodyPreview(value, maxLength = 220) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  return text.length > maxLength ? `${text.slice(0, maxLength).trimEnd()}...` : text;
}

function CommunicationPanel({ card, instanceUniqueId, templates = [] }) {
  const cardType = card?.cardType === "prospect" ? "prospect" : "order";
  const routeId = activityRouteIdForCard(card);
  const crmTemplates = useMemo(
    () => (templates || []).filter((template) => CRM_TEMPLATE_CATEGORIES.has(String(template.category || "").trim())),
    [templates]
  );
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [templatePreview, setTemplatePreview] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState("");
  const [draftSubject, setDraftSubject] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [draftSaving, setDraftSaving] = useState(false);
  const [error, setError] = useState("");
  const [communicationType, setCommunicationType] = useState("note");
  const [direction, setDirection] = useState("internal");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [toEmails, setToEmails] = useState("");
  const [ccEmails, setCcEmails] = useState("");
  const [trackingRef, setTrackingRef] = useState("");
  const [occurredAt, setOccurredAt] = useState(communicationDateInputValue);
  const [detailCommunication, setDetailCommunication] = useState(null);

  const loadRows = useCallback(async () => {
    if (!routeId) {
      setLoading(false);
      setError("This card cannot be linked to communications yet.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await fetchCrmCardCommunications(instanceUniqueId, cardType, routeId);
      setRows(Array.isArray(response?.data?.communications) ? response.data.communications : []);
    } catch (requestError) {
      const message = getFriendlyApiError(requestError, "Could not load communications.");
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [cardType, instanceUniqueId, routeId]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    const firstTemplateId = crmTemplates[0]?.id ? String(crmTemplates[0].id) : "";
    setTemplateId((current) => (current && crmTemplates.some((template) => String(template.id) === current) ? current : firstTemplateId));
  }, [crmTemplates]);

  useEffect(() => {
    let active = true;
    if (!templateId || !routeId) {
      setTemplatePreview(null);
      setDraftSubject("");
      setDraftBody("");
      setTemplateError("");
      return () => {
        active = false;
      };
    }

    setTemplateLoading(true);
    setTemplateError("");
    previewCrmEmailTemplate(instanceUniqueId, cardType, routeId, templateId)
      .then((result) => {
        if (!active) return;
        setTemplatePreview(result);
        setDraftSubject(result?.rendered?.subjectHtml || "");
        setDraftBody(result?.rendered?.bodyHtml || "");
      })
      .catch((requestError) => {
        if (!active) return;
        setTemplatePreview(null);
        setDraftSubject("");
        setDraftBody("");
        setTemplateError(getFriendlyApiError(requestError, "Could not preview CRM email template."));
      })
      .finally(() => {
        if (active) setTemplateLoading(false);
      });

    return () => {
      active = false;
    };
  }, [cardType, instanceUniqueId, routeId, templateId]);

  const handleTypeChange = (event) => {
    const nextType = event.target.value;
    setCommunicationType(nextType);
    if (["note", "internal_comment"].includes(nextType)) setDirection("internal");
  };

  const handleSubmit = async () => {
    if (!subject.trim() && !body.trim()) {
      toast.error("Add a subject or summary/body.");
      return;
    }
    const occurredDate = new Date(occurredAt);
    if (!occurredAt || Number.isNaN(occurredDate.getTime())) {
      toast.error("Choose a valid occurred date/time.");
      return;
    }
    setSubmitting(true);
    try {
      await createCrmCardCommunication(instanceUniqueId, cardType, routeId, {
        communicationType,
        direction,
        status: "logged",
        subject: subject || undefined,
        body: body || undefined,
        occurredAt: occurredDate.toISOString(),
        fromEmail: communicationType === "email" ? fromEmail || undefined : undefined,
        toEmails: communicationType === "email" ? toEmails : undefined,
        ccEmails: communicationType === "email" ? ccEmails : undefined,
        trackingRef: trackingRef || undefined,
      });
      setSubject("");
      setBody("");
      setFromEmail("");
      setToEmails("");
      setCcEmails("");
      setTrackingRef("");
      setOccurredAt(communicationDateInputValue());
      toast.success("Communication logged.");
      await loadRows();
    } catch (requestError) {
      toast.error(getFriendlyApiError(requestError, "Could not log communication."));
    } finally {
      setSubmitting(false);
    }
  };

  const copyText = async (value, label) => {
    try {
      await navigator.clipboard.writeText(String(value || ""));
      toast.success(`${label} copied.`);
    } catch (_error) {
      toast.error(`Could not copy ${label.toLowerCase()}.`);
    }
  };

  const handleSaveDraft = async () => {
    if (!templateId || !routeId) return;
    if (!draftSubject.trim() && !draftBody.trim()) {
      toast.error("Add a subject or body before saving a draft.");
      return;
    }
    setDraftSaving(true);
    try {
      await saveCrmEmailTemplateDraft(instanceUniqueId, cardType, routeId, {
        templateId,
        subject: draftSubject,
        body: draftBody,
        toEmails: templatePreview?.mergeFields?.contactEmail || "",
      });
      toast.success("Email draft saved. Sending is not enabled yet.");
      await loadRows();
    } catch (requestError) {
      toast.error(getFriendlyApiError(requestError, "Could not save CRM email draft."));
    } finally {
      setDraftSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        Communication records customer and team interactions. Activity History remains the system audit trail.
      </Alert>
      <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.5, backgroundColor: "#fff" }}>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} gap={1}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>Create Email Draft</Typography>
              <Typography variant="caption" color="text.secondary">Preview only - email sending is not enabled yet.</Typography>
            </Box>
            <TextField
              select
              SelectProps={{ native: true }}
              size="small"
              label="CRM template"
              value={templateId}
              onChange={(event) => setTemplateId(event.target.value)}
              sx={{ minWidth: { xs: "100%", sm: 280 } }}
              disabled={!crmTemplates.length}
            >
              {!crmTemplates.length ? <option value="">No CRM templates available</option> : null}
              {crmTemplates.map((template) => (
                <option key={template.id} value={template.id}>{template.name}</option>
              ))}
            </TextField>
          </Stack>
          {!crmTemplates.length ? (
            <Alert severity="warning">Create or enable CRM templates in Settings &gt; Company Settings &gt; Email Templates.</Alert>
          ) : null}
          {templateError ? <Alert severity="error">{templateError}</Alert> : null}
          {templateLoading ? <CircularProgress size={22} /> : null}
          {templatePreview ? (
            <Stack spacing={1.25}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1.25}>
                <TextField
                  size="small"
                  label="Draft subject"
                  value={draftSubject}
                  onChange={(event) => setDraftSubject(event.target.value)}
                  inputProps={{ maxLength: 255 }}
                  fullWidth
                />
                <Button variant="outlined" onClick={() => copyText(draftSubject, "Subject")} sx={{ alignSelf: { xs: "stretch", md: "center" } }}>
                  Copy Subject
                </Button>
              </Stack>
              <TextField
                size="small"
                label="Draft body"
                value={draftBody}
                onChange={(event) => setDraftBody(event.target.value)}
                multiline
                minRows={6}
                fullWidth
              />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button variant="outlined" onClick={() => copyText(draftBody, "Body")}>Copy Body</Button>
                <Button variant="contained" onClick={handleSaveDraft} disabled={draftSaving || !templateId || !routeId}>
                  {draftSaving ? "Saving..." : "Save Draft"}
                </Button>
              </Stack>
              <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.25, backgroundColor: "#fafafa" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.75 }}>Merged Preview</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                  Subject
                </Typography>
                <Box sx={{ mb: 1, overflowWrap: "anywhere" }} dangerouslySetInnerHTML={{ __html: draftSubject || "<i>(empty)</i>" }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                  Body
                </Typography>
                <Box sx={{ overflowWrap: "anywhere" }} dangerouslySetInnerHTML={{ __html: draftBody || "<i>(empty)</i>" }} />
              </Box>
              <Box sx={{ border: "1px dashed", borderColor: "divider", borderRadius: 1.5, p: 1.25 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 0.75 }}>Merge field values</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 0.75 }}>
                  {CRM_MERGE_FIELD_LABELS.map((field) => (
                    <Typography key={field} variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
                      <Box component="span" sx={{ fontFamily: "monospace", color: "text.primary" }}>{`{{${field}}}`}</Box>: {templatePreview.mergeFields?.[field] || ""}
                    </Typography>
                  ))}
                </Box>
              </Box>
            </Stack>
          ) : null}
        </Stack>
      </Box>
      <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.5, backgroundColor: "#fff" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 900, mb: 1.25 }}>Log communication</Typography>
        <Stack spacing={1.25}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
            <TextField select SelectProps={{ native: true }} size="small" label="Type" value={communicationType} onChange={handleTypeChange} fullWidth>
              <option value="note">Note</option>
              <option value="call">Call</option>
              <option value="email">Email log</option>
              <option value="meeting">Meeting</option>
              <option value="internal_comment">Internal comment</option>
            </TextField>
            <TextField select SelectProps={{ native: true }} size="small" label="Direction" value={direction} onChange={(event) => setDirection(event.target.value)} fullWidth>
              <option value="internal">Internal</option>
              <option value="outbound">Outbound</option>
              <option value="inbound">Inbound</option>
            </TextField>
            <TextField size="small" label="Occurred" type="datetime-local" value={occurredAt} onChange={(event) => setOccurredAt(event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          </Stack>
          <TextField size="small" label="Subject" value={subject} onChange={(event) => setSubject(event.target.value)} inputProps={{ maxLength: 255 }} fullWidth />
          <TextField
            size="small"
            label="Tracking reference (optional)"
            placeholder="[CRM-PROSPECT-123]"
            value={trackingRef}
            onChange={(event) => setTrackingRef(event.target.value)}
            inputProps={{ maxLength: 120 }}
            helperText="For future email-to-card matching; manual logs may leave this blank."
            fullWidth
          />
          <TextField size="small" label="Summary / body" value={body} onChange={(event) => setBody(event.target.value)} multiline minRows={3} fullWidth />
          {communicationType === "email" ? (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
              <TextField size="small" label="From" value={fromEmail} onChange={(event) => setFromEmail(event.target.value)} fullWidth />
              <TextField size="small" label="To (comma separated)" value={toEmails} onChange={(event) => setToEmails(event.target.value)} fullWidth />
              <TextField size="small" label="CC (comma separated)" value={ccEmails} onChange={(event) => setCcEmails(event.target.value)} fullWidth />
            </Stack>
          ) : null}
          <Button variant="contained" onClick={handleSubmit} disabled={submitting || !routeId} sx={{ alignSelf: "flex-start" }}>
            {submitting ? "Logging..." : "Log Communication"}
          </Button>
        </Stack>
      </Box>
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>Timeline</Typography>
          <Chip size="small" label={rows.length} />
        </Stack>
        {loading ? <CircularProgress size={22} /> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
        {!loading && !error && !rows.length ? <Alert severity="info">No communications logged yet.</Alert> : null}
        <Stack spacing={1}>
          {rows.map((row) => (
            <Box key={row.id} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5, p: 1.25, backgroundColor: "#fff" }}>
              <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={1}>
                <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" sx={{ fontWeight: 900 }}>{row.subject || titleize(row.communicationType)}</Typography>
                  <Chip size="small" label={titleize(row.communicationType)} />
                  <Chip size="small" variant="outlined" label={titleize(row.direction)} />
                  <Chip size="small" variant="outlined" label={titleize(row.status)} />
                  {row.trackingRef ? <Chip size="small" color="primary" variant="outlined" label={row.trackingRef} /> : null}
                  {row.sourceProvider ? <Chip size="small" variant="outlined" label={titleize(row.sourceProvider)} /> : null}
                  {row.threadId ? <Chip size="small" variant="outlined" label="Threaded" /> : null}
                  {row.hasAttachments ? <Chip size="small" color="info" variant="outlined" label="Attachments" /> : null}
                  {row.mappingMethod ? <Chip size="small" variant="outlined" label={titleize(row.mappingMethod)} /> : null}
                </Stack>
                <Typography variant="caption" color="text.secondary">{activityTimestamp(row.occurredAt)}</Typography>
              </Stack>
              {row.communicationType === "email" ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                  From: {row.fromEmail || "n/a"} · To: {row.toEmails?.join(", ") || "n/a"}{row.ccEmails?.length ? ` · CC: ${row.ccEmails.join(", ")}` : ""}
                </Typography>
              ) : null}
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.4 }}>Logged by: {row.createdByName || "Unknown"}</Typography>
              {row.body ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, overflowWrap: "anywhere" }}>
                  {communicationBodyPreview(row.body)}
                </Typography>
              ) : null}
              <Button size="small" variant="text" onClick={() => setDetailCommunication(row)} sx={{ mt: 0.5, px: 0.5 }}>
                View Details
              </Button>
            </Box>
          ))}
        </Stack>
      </Box>
      <Alert severity="info">
        Mailbox messages are grouped by provider thread where available. Unmatched or ambiguous mailbox messages stay in manager review.
      </Alert>
      <Dialog open={Boolean(detailCommunication)} onClose={() => setDetailCommunication(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ pr: 6 }}>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            {detailCommunication?.subject || titleize(detailCommunication?.communicationType || "communication")}
          </Typography>
          <IconButton aria-label="Close communication details" onClick={() => setDetailCommunication(null)} sx={{ position: "absolute", right: 12, top: 12 }}>
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={titleize(detailCommunication?.communicationType)} />
            <Chip size="small" variant="outlined" label={titleize(detailCommunication?.direction)} />
            <Chip size="small" variant="outlined" label={titleize(detailCommunication?.status)} />
            {detailCommunication?.trackingRef ? <Chip size="small" color="primary" variant="outlined" label={detailCommunication.trackingRef} /> : null}
            {detailCommunication?.sourceProvider ? <Chip size="small" variant="outlined" label={titleize(detailCommunication.sourceProvider)} /> : null}
          </Stack>
          <InfoGrid rows={[
            ["Subject", detailCommunication?.subject],
            ["Occurred", activityTimestamp(detailCommunication?.occurredAt)],
            ["Created by", detailCommunication?.createdByName || (detailCommunication?.createdByUserId ? `User #${detailCommunication.createdByUserId}` : "Unknown")],
            ["Direction", titleize(detailCommunication?.direction)],
            ["Status", titleize(detailCommunication?.status)],
            ["Provider / source", detailCommunication?.sourceProvider || detailCommunication?.metadata?.source],
            ["Mapping method", detailCommunication?.mappingMethod || detailCommunication?.metadata?.mapping?.matchedBy],
            ["Mapping confidence", detailCommunication?.mappingConfidence || detailCommunication?.metadata?.mapping?.confidence],
            ["Mailbox source", detailCommunication?.metadata?.mailboxScope || detailCommunication?.metadata?.emailAccountId],
            ["Tracking reference", detailCommunication?.trackingRef],
            ["External message ID", detailCommunication?.externalMessageId || detailCommunication?.metadata?.externalMessageId],
            ["Thread ID", detailCommunication?.threadId || detailCommunication?.metadata?.threadId],
            ["From", detailCommunication?.fromEmail],
            ["To", detailCommunication?.toEmails?.join(", ")],
            ["CC", detailCommunication?.ccEmails?.join(", ")],
            ["Attachments", detailCommunication?.hasAttachments ? `${detailCommunication?.attachments?.length || 0} attachment metadata item(s)` : "No"],
          ]} />
          <Box>
            <Typography variant="caption" color="text.secondary">Full message / summary</Typography>
            <Box sx={{ mt: 0.5, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1.5, backgroundColor: "#f8fafc", maxHeight: "45vh", overflowY: "auto" }}>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                {detailCommunication?.body || "No message body was recorded."}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailCommunication(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function PresentationHistoryPanel({ instanceUniqueId, orderId, rows, loading, error, projectPresentationPath, onSend }) {
  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} gap={1}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>Presentation History</Typography>
          <Chip size="small" label={rows?.length || 0} />
        </Stack>
        <Button component={Link} to={projectPresentationPath} {...newTabLinkProps} variant="outlined" endIcon={<OpenInNewRoundedIcon />}>
          Open Project Presentation Page
        </Button>
      </Stack>

      {loading ? <CircularProgress size={24} /> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
      {!loading && !error && !rows?.length ? (
        <Alert severity="info">No presentations created yet.</Alert>
      ) : null}

      {rows?.length ? (
        <Stack spacing={1.25}>
          {rows.map((presentation, index) => {
            const presentationId = presentation.presentationId || presentation.id;
            const presentationPath = `/instance/${instanceUniqueId}/presentations/${presentationId}`;
            const status = String(presentation.status || "");
            const isSent = status === "Presentation Sent";
            const canSendRow =
              presentation.hasActiveProducts !== false &&
              index === 0 &&
              ["Presentation Draft", "Presentation Sent"].includes(status) &&
              presentationId;
            return (
              <Box
                key={presentationId || `${presentation.orderId}-${presentation.createdAt}`}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                  p: 1.25,
                  backgroundColor: "#fff",
                }}
              >
                <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={1.25}>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
                      <Typography variant="body2" sx={{ fontWeight: 900 }}>
                        Presentation #{presentationId || "n/a"}
                      </Typography>
                      <Chip size="small" label={presentation.status || "Presentation Draft"} />
                      {presentation.hidePrice ? <Chip size="small" variant="outlined" label="Pricing hidden" /> : null}
                      {presentation.productCount != null ? <Chip size="small" label={`${presentation.productCount} product${Number(presentation.productCount) === 1 ? "" : "s"}`} /> : null}
                      {presentation.hasActiveProducts === false ? <Chip size="small" color="warning" label="Products removed" /> : null}
                    </Stack>
                    <InfoGrid rows={[
                      ["Date", shortDate(presentation.presentationDate || presentation.createdAt)],
                      ["Contact", presentation.clientContactEmail],
                      ["Total", presentation.total == null ? "n/a" : currency(presentation.total)],
                      ["Updated", shortDate(presentation.updatedAt)],
                    ]} />
                    {presentation.hasActiveProducts === false ? (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        Current cart has changed. Create a new document from the current cart before sending.
                      </Alert>
                    ) : null}
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                    {canSendRow ? (
                      <Button variant="contained" size="small" onClick={() => onSend?.(presentation, { resend: isSent })}>
                        {isSent ? "Resend Presentation" : "Send Presentation"}
                      </Button>
                    ) : null}
                    {presentationId ? (
                      <Button component={Link} to={presentationPath} {...newTabLinkProps} variant="outlined" size="small" endIcon={<OpenInNewRoundedIcon />}>
                        Open Presentation
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      ) : null}
    </Stack>
  );
}

const estimateStatusLabel = (status) => {
  if (String(status) === "1") return "Estimate Draft";
  if (String(status) === "1_sent") return "Estimate Sent";
  if (String(status) === "3") return "Client Approved";
  if (String(status) === "4") return "Client Change Requested";
  return status || "n/a";
};

const readProductsArray = (products) => {
  if (Array.isArray(products)) return products;
  if (!products || typeof products !== "string") return [];
  try {
    const parsed = JSON.parse(products);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
};

const estimateTotalValue = (estimate) => {
  const direct = Number(estimate?.totalValue ?? estimate?.total ?? estimate?.Total?.amount);
  if (Number.isFinite(direct) && direct > 0) return direct;

  const products = readProductsArray(estimate?.products);
  const total = products.reduce((sum, product) => {
    const value = Number(product?.total ?? product?.Subtotal ?? product?.subtotal ?? product?.price ?? 0);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);
  return total > 0 ? total : null;
};

function EstimateHistoryPanel({ instanceUniqueId, orderId, rows, loading, error, projectEstimatePath, onSend }) {
  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} gap={1}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>Estimate History</Typography>
          <Chip size="small" label={rows?.length || 0} />
        </Stack>
        <Button component={Link} to={projectEstimatePath} {...newTabLinkProps} variant="outlined" endIcon={<OpenInNewRoundedIcon />}>
          Open Project Estimate Page
        </Button>
      </Stack>

      {loading ? <CircularProgress size={24} /> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
      {!loading && !error && !rows?.length ? (
        <Alert severity="info">No estimates created yet.</Alert>
      ) : null}

      {rows?.length ? (
        <Stack spacing={1.25}>
          {rows.map((estimate) => {
            const estimateId = estimate.estimationId || estimate.id;
            const estimatePath = estimateId ? orderPath(instanceUniqueId, orderId, "estimation", estimateId) : projectEstimatePath;
            const total = estimateTotalValue(estimate);
            const status = String(estimate.status);
            const isSent = status === "1_sent";
            const canSendRow = estimate.hasActiveProducts !== false && ["1", "1_sent"].includes(status) && estimateId;
            return (
              <Box
                key={estimateId || `${orderId}-${estimate.createdAt}`}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                  p: 1.25,
                  backgroundColor: "#fff",
                }}
              >
                <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={1.25}>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
                      <Typography variant="body2" sx={{ fontWeight: 900 }}>
                        Estimate #{estimateId || "n/a"}
                      </Typography>
                      <Chip size="small" label={estimateStatusLabel(estimate.status)} />
                      {estimate.hasActiveProducts === false ? <Chip size="small" color="warning" label="Products removed" /> : null}
                    </Stack>
                    <InfoGrid rows={[
                      ["Document date", shortDate(estimate.orderDate || estimate.inhandsDate)],
                      ["Status", estimateStatusLabel(estimate.status)],
                      ["Created", shortDate(estimate.createdAt)],
                      ["Updated", shortDate(estimate.updatedAt)],
                      ["Amount", total == null ? "n/a" : currency(total)],
                    ]} />
                    {estimate.hasActiveProducts === false ? (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        Current cart has changed. Create a new document from the current cart before sending.
                      </Alert>
                    ) : null}
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                    {canSendRow ? (
                      <Button variant="contained" size="small" onClick={() => onSend?.(estimate, { resend: isSent })}>
                        {isSent ? "Resend Estimate" : "Send Estimate"}
                      </Button>
                    ) : null}
                    {estimateId ? (
                      <Button component={Link} to={estimatePath} {...newTabLinkProps} variant="outlined" size="small" endIcon={<OpenInNewRoundedIcon />}>
                        Open Estimate in New Tab
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      ) : null}
    </Stack>
  );
}

const salesOrderStatusLabel = (status) => {
  if (String(status) === "2") return "Agreement Pending";
  if (String(status) === "2_sent") return "Agreement Sent";
  if (String(status) === "3") return "Client Approved";
  if (String(status) === "4") return "Client Change Requested";
  if (String(status) === "5") return "Closed";
  if (String(status) === "6") return "In Production";
  if (String(status) === "7") return "Shipped";
  if (String(status) === "8") return "Ready to be Invoiced";
  if (String(status) === "9") return "Invoiced";
  if (String(status) === "10") return "Paid";
  return status || "n/a";
};

function SalesOrderHistoryPanel({ instanceUniqueId, orderId, rows, loading, error, projectSalesOrderPath, onSend }) {
  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} gap={1}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>Sales Order History</Typography>
          <Chip size="small" label={rows?.length || 0} />
        </Stack>
        <Button component={Link} to={projectSalesOrderPath} {...newTabLinkProps} variant="outlined" endIcon={<OpenInNewRoundedIcon />}>
          Open Project Sales Order Page
        </Button>
      </Stack>

      {loading ? <CircularProgress size={24} /> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
      {!loading && !error && !rows?.length ? (
        <Alert severity="info">No sales orders created yet.</Alert>
      ) : null}

      {rows?.length ? (
        <Stack spacing={1.25}>
          {rows.map((salesOrder) => {
            const salesOrderId = salesOrder.estimationId || salesOrder.id;
            const salesOrderPath = salesOrderId ? orderPath(instanceUniqueId, orderId, "sales-order", salesOrderId) : projectSalesOrderPath;
            const total = estimateTotalValue(salesOrder);
            const status = String(salesOrder.status);
            const isSent = status === "2_sent";
            const canSendRow = salesOrder.hasActiveProducts !== false && ["2", "2_sent"].includes(status) && salesOrderId;
            return (
              <Box
                key={salesOrderId || `${orderId}-${salesOrder.createdAt}`}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                  p: 1.25,
                  backgroundColor: "#fff",
                }}
              >
                <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={1.25}>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.75 }}>
                      <Typography variant="body2" sx={{ fontWeight: 900 }}>
                        Sales Order #{salesOrderId || "n/a"}
                      </Typography>
                      <Chip size="small" label={salesOrderStatusLabel(salesOrder.status)} />
                      {salesOrder.hasActiveProducts === false ? <Chip size="small" color="warning" label="Products removed" /> : null}
                    </Stack>
                    <InfoGrid rows={[
                      ["Document date", shortDate(salesOrder.orderDate || salesOrder.inhandsDate)],
                      ["Status", salesOrderStatusLabel(salesOrder.status)],
                      ["Created", shortDate(salesOrder.createdAt)],
                      ["Updated", shortDate(salesOrder.updatedAt)],
                      ["Amount", total == null ? "n/a" : currency(total)],
                    ]} />
                    {salesOrder.hasActiveProducts === false ? (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        Current cart has changed. Create a new document from the current cart before sending.
                      </Alert>
                    ) : null}
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                    {canSendRow ? (
                      <Button variant="contained" size="small" onClick={() => onSend?.(salesOrder, { resend: isSent })}>
                        {isSent ? "Resend Agreement" : "Send Agreement"}
                      </Button>
                    ) : null}
                    {salesOrderId ? (
                      <Button component={Link} to={salesOrderPath} {...newTabLinkProps} variant="outlined" size="small" endIcon={<OpenInNewRoundedIcon />}>
                        Open Sales Order in New Tab
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      ) : null}
    </Stack>
  );
}

function PipelineBoard({
  instanceUniqueId,
  embedded = false,
  initialQuery = "",
  height = "calc(100vh - 215px)",
  pageSize = 25,
  showHeader = true,
  boardMode = "orders",
  crmScope = "my",
  selectedUserId = null,
  selectedUserName = "",
  currentUserId = null,
  isCrmManager = false,
  users = [],
  refreshKey = 0,
  onWorkspaceOpen,
  assignedTo = null,
  enableAutoRefresh = false,
}) {
  const history = useHistory();
  const isCrmMode = boardMode === "crm";
  const [groups, setGroups] = useState(isCrmMode ? emptyCrmGroups : emptyGroups);
  const [summary, setSummary] = useState(null);
  const [query, setQuery] = useState(initialQuery);
  const [attentionFilter, setAttentionFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [tagDefinitions, setTagDefinitions] = useState([]);
  const [needsAttentionOpen, setNeedsAttentionOpen] = useState(false);
  const [managementReportsOpen, setManagementReportsOpen] = useState(false);
  const [repAlertsOpen, setRepAlertsOpen] = useState(false);
  const [unmappedReviewOpen, setUnmappedReviewOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [laneLoading, setLaneLoading] = useState({});
  const [laneErrors, setLaneErrors] = useState({});
  const [workspace, setWorkspace] = useState({ card: null, tab: 0 });
  const [fastTransition, setFastTransition] = useState(null);
  const [lossTransition, setLossTransition] = useState(null);
  const [lossOutcome, setLossOutcome] = useState("Lost");
  const [lossReason, setLossReason] = useState("");
  const [lossNotes, setLossNotes] = useState("");
  const [lossSubmitting, setLossSubmitting] = useState(false);
  const [reviveTarget, setReviveTarget] = useState(null);
  const [reviveReason, setReviveReason] = useState("");
  const [reviveNotes, setReviveNotes] = useState("");
  const [reviveSubmitting, setReviveSubmitting] = useState(false);
  const [moveSubmitting, setMoveSubmitting] = useState(false);
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const dragRef = useRef(null);
  const dragImageRef = useRef(null);
  const boardScrollerRef = useRef(null);
  const dragScrollFrameRef = useRef(null);
  const dragScrollSpeedRef = useRef(0);
  const suppressNextClickRef = useRef(false);
  const refreshBlockedRef = useRef(false);
  const lastAutomaticRefreshAtRef = useRef(0);
  const appliedQueryRef = useRef(initialQuery || "");
  const initialQueryRef = useRef(initialQuery || "");
  const columns = useMemo(() => {
    if (!isCrmMode) return [...ACTIVE_COLUMNS, WIN_COLUMN, LOSS_COLUMN];
    const showAllLeads = isCrmManager && (crmScope === "all" || crmScope === "unassigned");
    return [
      ...(showAllLeads ? [ALL_LEADS_COLUMN] : []),
      MY_LEADS_COLUMN,
      ...ACTIVE_COLUMNS,
      WIN_COLUMN,
      LOSS_COLUMN,
    ];
  }, [crmScope, isCrmManager, isCrmMode]);
  const fallbackGroups = useMemo(() => isCrmMode ? emptyCrmGroups() : emptyGroups(), [isCrmMode]);
  const visibleGroups = groups;
  const allBoardCards = useMemo(() => cardsFromGroups(groups), [groups]);
  const needsAttentionCards = useMemo(
    () => allBoardCards
      .filter((card) => cardMatchesAttentionFilter(card, "needs_attention"))
      .sort((a, b) => Number(b.attentionScore || 0) - Number(a.attentionScore || 0)),
    [allBoardCards]
  );
  const attentionScopeLabel = crmScope === "all"
    ? "All pipelines"
    : crmScope === "user"
      ? (selectedUserName || `User #${selectedUserId}`)
      : "My Pipeline";

  const openWorkspace = useCallback((card, tab = null) => {
    if (suppressNextClickRef.current) return;
    onWorkspaceOpen?.();
    setWorkspace({
      card,
      tab: Number.isInteger(tab) ? tab : initialWorkspaceTabForCard(card),
    });
  }, [onWorkspaceOpen]);

  const closeWorkspace = useCallback(() => {
    setWorkspace({ card: null, tab: 0 });
  }, []);

  const openAttentionCard = useCallback((card) => {
    setNeedsAttentionOpen(false);
    openWorkspace(card);
  }, [openWorkspace]);

  const openReportCard = useCallback((card) => {
    setManagementReportsOpen(false);
    setRepAlertsOpen(false);
    openWorkspace(card);
  }, [openWorkspace]);

  const fetchBoard = useCallback(async (nextQuery = "") => {
    appliedQueryRef.current = nextQuery || "";
    setLoading(true);
    setError("");
    try {
      const data = await retryTransientRequest(
        async () => isCrmMode
          ? (await fetchCrmPipelineBoard({
              instanceUniqueId,
              scope: crmScope,
              userId: selectedUserId || undefined,
              q: nextQuery || undefined,
              includeWinLoss: true,
              limit: pageSize,
              attentionFilter: attentionFilter || undefined,
              tagId: tagFilter || undefined,
            })).data
          : await fetchPipelineOrders({
              instanceUniqueId,
              view: "kanban-lite",
              q: nextQuery || undefined,
              assignedTo: assignedTo || undefined,
              includePostSale: true,
              pageSize,
            }),
        { retries: 1 }
      );
      setGroups(isCrmMode ? normalizeCrmBoardData(data) : (data.groups || emptyGroups()));
      setSummary(data.summary || null);
      if (isCrmMode) setTagDefinitions(Array.isArray(data.tagDefinitions) ? data.tagDefinitions : []);
    } catch (err) {
      if (isInternalApiError(err)) console.warn("CRM pipeline load failed:", err);
      setError(getFriendlyApiError(err, "Failed to load CRM pipeline."));
    } finally {
      setLoading(false);
    }
  }, [assignedTo, attentionFilter, crmScope, instanceUniqueId, isCrmMode, pageSize, selectedUserId, tagFilter]);

  const loadMoreLane = useCallback(async (laneKey) => {
    if (!isCrmMode) return;
    const current = groups[laneKey];
    if (!current?.hasMore || laneLoading[laneKey]) return;
    setLaneLoading((state) => ({ ...state, [laneKey]: true }));
    setLaneErrors((state) => ({ ...state, [laneKey]: "" }));
    try {
      const response = await fetchCrmPipelineBoard({
        instanceUniqueId,
        scope: crmScope,
        userId: selectedUserId || undefined,
        q: appliedQueryRef.current || undefined,
        lane: laneKey,
        page: Number(current.page || 1) + 1,
        limit: pageSize,
        attentionFilter: attentionFilter || undefined,
        tagId: tagFilter || undefined,
        includeWinLoss: true,
      });
      const incoming = normalizeCrmBoardData(response.data)[laneKey];
      setGroups((state) => {
        const existing = state[laneKey] || {};
        const seen = new Set((existing.cards || []).map((card) => String(cardDomId(card))));
        const appended = (incoming.cards || []).filter((card) => !seen.has(String(cardDomId(card))));
        const cards = [...(existing.cards || []), ...appended];
        return { ...state, [laneKey]: { ...existing, ...incoming, cards, loadedCount: cards.length } };
      });
    } catch (err) {
      setLaneErrors((state) => ({ ...state, [laneKey]: getFriendlyApiError(err, "Could not load more cards.") }));
    } finally {
      setLaneLoading((state) => ({ ...state, [laneKey]: false }));
    }
  }, [attentionFilter, crmScope, groups, instanceUniqueId, isCrmMode, laneLoading, pageSize, selectedUserId, tagFilter]);

  const loadPipeline = useCallback((override = {}) => {
    return fetchBoard((override.q ?? query) || "");
  }, [fetchBoard, query]);

  const handleStaleCardConflict = useCallback(async (err) => {
    if (Number(err?.response?.status) !== 409) return false;
    toast.error(STALE_CARD_MESSAGE);
    await loadPipeline();
    return true;
  }, [loadPipeline]);

  refreshBlockedRef.current = Boolean(
    loading ||
    draggingId ||
    workspace.card ||
    fastTransition ||
    lossTransition ||
    reviveTarget ||
    moveSubmitting ||
    lossSubmitting ||
    reviveSubmitting
  );

  useEffect(() => {
    if (initialQueryRef.current !== (initialQuery || "")) {
      initialQueryRef.current = initialQuery || "";
      appliedQueryRef.current = initialQuery || "";
      setQuery(initialQuery || "");
    }
    fetchBoard(appliedQueryRef.current);
  }, [fetchBoard, initialQuery, refreshKey]);

  useEffect(() => {
    if (!enableAutoRefresh) return undefined;

    const refreshWhenAvailable = () => {
      if (document.visibilityState !== "visible" || refreshBlockedRef.current) return;
      const now = Date.now();
      if (now - lastAutomaticRefreshAtRef.current < AUTO_REFRESH_DEBOUNCE_MS) return;
      lastAutomaticRefreshAtRef.current = now;
      loadPipeline();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshWhenAvailable();
    };

    window.addEventListener("focus", refreshWhenAvailable);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    const intervalId = window.setInterval(refreshWhenAvailable, AUTO_REFRESH_INTERVAL_MS);

    return () => {
      window.removeEventListener("focus", refreshWhenAvailable);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [enableAutoRefresh, loadPipeline]);

  useEffect(() => {
    document.body.style.cursor = draggingId ? "grabbing" : "";
    return () => {
      document.body.style.cursor = "";
    };
  }, [draggingId]);

  const stopDragAutoScroll = useCallback(() => {
    dragScrollSpeedRef.current = 0;
    if (dragScrollFrameRef.current !== null) {
      window.cancelAnimationFrame(dragScrollFrameRef.current);
      dragScrollFrameRef.current = null;
    }
  }, []);

  const runDragAutoScroll = useCallback(() => {
    const scroller = boardScrollerRef.current;
    const speed = dragScrollSpeedRef.current;
    if (!scroller || !speed || !dragRef.current) {
      dragScrollFrameRef.current = null;
      return;
    }

    const maxScrollLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const nextScrollLeft = Math.max(0, Math.min(maxScrollLeft, scroller.scrollLeft + speed));
    if (nextScrollLeft !== scroller.scrollLeft) scroller.scrollLeft = nextScrollLeft;
    dragScrollFrameRef.current = window.requestAnimationFrame(runDragAutoScroll);
  }, []);

  const updateDragAutoScroll = useCallback((event) => {
    const scroller = boardScrollerRef.current;
    if (!dragRef.current || !scroller) {
      stopDragAutoScroll();
      return;
    }

    const rect = scroller.getBoundingClientRect();
    if (event.clientY < rect.top || event.clientY > rect.bottom) {
      stopDragAutoScroll();
      return;
    }

    const leftDistance = event.clientX - rect.left;
    const rightDistance = rect.right - event.clientX;
    let speed = 0;
    if (leftDistance >= 0 && leftDistance < DRAG_SCROLL_EDGE_PX) {
      speed = -DRAG_SCROLL_MAX_PX_PER_FRAME * (1 - leftDistance / DRAG_SCROLL_EDGE_PX);
    } else if (rightDistance >= 0 && rightDistance < DRAG_SCROLL_EDGE_PX) {
      speed = DRAG_SCROLL_MAX_PX_PER_FRAME * (1 - rightDistance / DRAG_SCROLL_EDGE_PX);
    }

    dragScrollSpeedRef.current = speed;
    if (!speed) {
      stopDragAutoScroll();
    } else if (dragScrollFrameRef.current === null) {
      dragScrollFrameRef.current = window.requestAnimationFrame(runDragAutoScroll);
    }
  }, [runDragAutoScroll, stopDragAutoScroll]);

  useEffect(() => {
    if (!draggingId) return undefined;
    window.addEventListener("dragover", updateDragAutoScroll);
    return () => {
      window.removeEventListener("dragover", updateDragAutoScroll);
      stopDragAutoScroll();
    };
  }, [draggingId, stopDragAutoScroll, updateDragAutoScroll]);

  const clearNativeDrag = useCallback(() => {
    stopDragAutoScroll();
    removeDragImageNode(dragImageRef.current);
    dragImageRef.current = null;
    dragRef.current = null;
    setDraggingId(null);
    setDragOverColumn(null);
    window.setTimeout(() => {
      suppressNextClickRef.current = false;
    }, 120);
  }, [stopDragAutoScroll]);

  const buildDropPlan = useCallback(() => {
    const drag = dragRef.current;
    if (!drag?.cardId) return null;
    const current = findCardInGroups(groups, drag.cardId);
    const card = current?.card;
    if (!canDragPipelineCard(card, isCrmMode)) return null;

    const targetColumnKey = drag.overColumnKey || drag.fromColumnKey;
    const targetColumn = columns.find((column) => column.key === targetColumnKey);
    if (!targetColumn) return null;
    const canMoveOrderToColumn =
      ACTIVE_COLUMNS.some((column) => column.key === targetColumn.key) ||
      (targetColumn.key === "loss" && ACTIVE_COLUMNS.some((column) => column.key === card.pipelineColumn));
    if (isOrderCard(card) && !canMoveOrderToColumn) return null;

    const targetCardsWithoutActive = cardsForColumn(groups, targetColumn.key)
      .filter((item) => String(cardDomId(item)) !== String(cardDomId(card)));
    let targetIndex = Number.isInteger(drag.overIndex) ? drag.overIndex : targetCardsWithoutActive.length;

    if (drag.overCardId) {
      const overIndex = targetCardsWithoutActive.findIndex((item) => String(cardDomId(item)) === String(drag.overCardId));
      targetIndex = overIndex === -1 ? targetCardsWithoutActive.length : overIndex + (drag.insertAfter ? 1 : 0);
    }

    targetIndex = Math.max(0, Math.min(targetIndex, targetCardsWithoutActive.length));
    const beforeCard = targetCardsWithoutActive[targetIndex - 1] || null;
    const afterCard = targetCardsWithoutActive[targetIndex] || null;
    let isSamePosition = current?.columnKey === targetColumn.key;
    if (isSamePosition) {
      const originalIds = cardsForColumn(groups, targetColumn.key).map((item) => String(cardDomId(item)));
      const nextIds = targetCardsWithoutActive.map((item) => String(cardDomId(item)));
      nextIds.splice(targetIndex, 0, String(cardDomId(card)));
      isSamePosition = originalIds.length === nextIds.length && originalIds.every((id, index) => id === nextIds[index]);
    }

    const beforeOrderCard = beforeCard && isOrderCard(beforeCard) ? beforeCard : null;
    const afterOrderCard = afterCard && isOrderCard(afterCard) ? afterCard : null;
    return {
      card,
      targetColumn,
      targetIndex,
      beforeOrderId: beforeOrderCard?.orderId || null,
      afterOrderId: afterOrderCard?.orderId || null,
      isSamePosition,
    };
  }, [columns, groups, isCrmMode]);

  const handleNativeDragStart = useCallback((event, card, columnKey, index) => {
    if (!canDragPipelineCard(card, isCrmMode)) {
      event.preventDefault();
      return;
    }
    const dragCardId = cardDomId(card);
    dragRef.current = {
      cardId: dragCardId,
      fromColumnKey: columnKey,
      fromIndex: index,
      overColumnKey: columnKey,
      overIndex: index,
      overCardId: null,
      insertAfter: false,
    };
    setDraggingId(String(dragCardId));
    setDragOverColumn(columnKey);
    suppressNextClickRef.current = true;
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("text/plain", String(dragCardId));
    removeDragImageNode(dragImageRef.current);
    const dragImage = buildPipelineDragImage(event.currentTarget, card);
    dragImageRef.current = dragImage;
    const rect = event.currentTarget.getBoundingClientRect();
    const offsetX = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
    const offsetY = Math.max(0, Math.min(event.clientY - rect.top, rect.height));
    event.dataTransfer.setDragImage(dragImage, offsetX, offsetY);
    window.requestAnimationFrame(() => {
      if (dragImageRef.current === dragImage) {
        removeDragImageNode(dragImage);
        dragImageRef.current = null;
      }
    });
  }, [isCrmMode]);

  const handleNativeCardDragOver = useCallback((event, card, columnKey, index) => {
    const drag = dragRef.current;
    const dragged = drag?.cardId ? findCardInGroups(groups, drag.cardId)?.card : null;
    if (!drag?.cardId || !dragged || !canDragPipelineCard(dragged, isCrmMode)) return;
    const targetColumn = columns.find((column) => column.key === columnKey);
    const isActiveOrderToLoss =
      isOrderCard(dragged) &&
      columnKey === "loss" &&
      ACTIVE_COLUMNS.some((column) => column.key === dragged.pipelineColumn);
    if (!targetColumn || (targetColumn.readOnly && !isActiveOrderToLoss)) return;
    if (isOrderCard(dragged) && !ACTIVE_COLUMNS.some((column) => column.key === columnKey) && !isActiveOrderToLoss) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = isCrmMode && crmInvalidDropReason(dragged, columnKey) ? "none" : "move";
    const rect = event.currentTarget.getBoundingClientRect();
    const insertAfter = event.clientY > rect.top + rect.height / 2;
    drag.overColumnKey = columnKey;
    drag.overCardId = String(cardDomId(card));
    drag.insertAfter = insertAfter;
    drag.overIndex = index + (insertAfter ? 1 : 0);
    if (dragOverColumn !== columnKey) setDragOverColumn(columnKey);
  }, [columns, dragOverColumn, groups, isCrmMode]);

  const handleNativeLaneDragOver = useCallback((event, columnKey) => {
    const drag = dragRef.current;
    if (!drag?.cardId) return;
    const dragged = findCardInGroups(groups, drag.cardId)?.card;
    if (!dragged || !canDragPipelineCard(dragged, isCrmMode)) return;
    const targetColumn = columns.find((column) => column.key === columnKey);
    const isActiveOrderToLoss =
      isOrderCard(dragged) &&
      columnKey === "loss" &&
      ACTIVE_COLUMNS.some((column) => column.key === dragged.pipelineColumn);
    if (!targetColumn || (targetColumn.readOnly && !isActiveOrderToLoss)) return;
    if (event.target?.closest?.("[data-pipeline-card='true']")) return;
    if (groups?.[columnKey]?.hasMore) {
      event.dataTransfer.dropEffect = "none";
      return;
    }
    if (isOrderCard(dragged) && !ACTIVE_COLUMNS.some((column) => column.key === columnKey) && !isActiveOrderToLoss) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = isCrmMode && crmInvalidDropReason(dragged, columnKey) ? "none" : "move";
    const visibleCount = cardsForColumn(groups, columnKey)
      .slice(0, pageSize)
      .filter((item) => String(cardDomId(item)) !== String(drag.cardId)).length;
    drag.overColumnKey = columnKey;
    drag.overCardId = null;
    drag.insertAfter = false;
    drag.overIndex = visibleCount;
    if (dragOverColumn !== columnKey) setDragOverColumn(columnKey);
  }, [columns, dragOverColumn, groups, isCrmMode, pageSize]);

  const handleNativeDrop = useCallback(async (event, columnKey) => {
    event.preventDefault();
    if (dragRef.current) dragRef.current.overColumnKey = dragRef.current.overColumnKey || columnKey;
    const plan = buildDropPlan();
    clearNativeDrag();
    if (!plan || plan.isSamePosition) return;
    const { card, targetColumn, targetIndex, beforeOrderId, afterOrderId } = plan;

    if (isCrmMode && isProspectCard(card)) {
      toast.info(crmInvalidDropReason(card, targetColumn.key));
      return;
    }

    if (
      isOrderCard(card) &&
      targetColumn.key === "loss" &&
      ACTIVE_COLUMNS.some((column) => column.key === card.pipelineColumn)
    ) {
      setLossOutcome("Lost");
      setLossReason("");
      setLossNotes("");
      setLossTransition({ card, targetIndex, beforeOrderId, afterOrderId });
      return;
    }

    const previous = groups;
    const nextSoStatus = card.pipelineColumn === targetColumn.key ? card.soStatus : targetColumn.moveStatus;
    const transitionIntent = getPipelineTransitionIntent({
      fromColumnId: card.pipelineColumn,
      toColumnId: targetColumn.key,
      fromStatus: card.soStatus,
      toStatus: nextSoStatus,
    });

    if (isOrderCard(card) && transitionIntent.requiresFastPopup) {
      setFastTransition({
        card,
        sourceLane: card.pipelineColumn,
        sourceStatus: card.soStatus,
        targetLane: targetColumn.key,
        targetStatus: nextSoStatus,
        documentType: documentTypeForTargetColumn(targetColumn.key),
        beforeOrderId,
        afterOrderId,
      });
      return;
    }

    setGroups((current) => moveCardInGroups(current, cardDomId(card), targetColumn.key, nextSoStatus, targetIndex));
    try {
      setMoveSubmitting(true);
      const response = await updatePipelineStage(card.orderId, {
        instanceUniqueId,
        soStatus: nextSoStatus,
        targetColumn: targetColumn.key,
        beforeOrderId,
        afterOrderId,
        expectedStatus: card.soStatus,
        expectedStage: card.pipelineColumn,
        expectedUpdatedAt: card.updatedAt || undefined,
      });
      setGroups((current) => moveCardInGroups(current, cardDomId(card), targetColumn.key, response.soStatus || nextSoStatus, targetIndex, {
        crmPipelineRank: response.crmPipelineRank,
        crmPipelineRankUpdatedAt: response.crmPipelineRankUpdatedAt,
        updatedAt: response.updatedAt,
      }));
      toast.success(card.pipelineColumn === targetColumn.key ? `Reordered #${card.orderId}` : `Moved #${card.orderId} to ${targetColumn.label}`);
      await loadPipeline();
    } catch (err) {
      setGroups(previous);
      if (await handleStaleCardConflict(err)) return;
      if (isInternalApiError(err)) console.warn("Pipeline card move failed:", err);
      toast.error(getFriendlyApiError(err, "Could not move pipeline card."));
    } finally {
      setMoveSubmitting(false);
    }
  }, [buildDropPlan, clearNativeDrag, groups, handleStaleCardConflict, instanceUniqueId, isCrmMode, loadPipeline]);

  const closeLossTransition = useCallback(() => {
    if (lossSubmitting) return;
    setLossTransition(null);
    setLossReason("");
    setLossNotes("");
  }, [lossSubmitting]);

  const confirmLossTransition = useCallback(async () => {
    if (!lossTransition?.card || !lossReason) return;
    try {
      setLossSubmitting(true);
      const response = await updatePipelineStage(lossTransition.card.orderId, {
        instanceUniqueId,
        soStatus: lossOutcome,
        targetColumn: "loss",
        beforeOrderId: lossTransition.beforeOrderId,
        afterOrderId: lossTransition.afterOrderId,
        lossReason,
        lossNotes,
        expectedStatus: lossTransition.card.soStatus,
        expectedStage: lossTransition.card.pipelineColumn,
        expectedUpdatedAt: lossTransition.card.updatedAt || undefined,
      });
      setLossTransition(null);
      setLossReason("");
      setLossNotes("");
      toast.success(`Moved #${lossTransition.card.orderId} to LOSS.`);
      await loadPipeline();
      if (workspace.card && String(workspace.card.orderId) === String(lossTransition.card.orderId)) {
        setWorkspace({ card: null, tab: 0 });
      }
      return response;
    } catch (err) {
      if (await handleStaleCardConflict(err)) {
        setLossTransition(null);
        setLossReason("");
        setLossNotes("");
        return;
      }
      if (isInternalApiError(err)) console.warn("Move to LOSS failed:", err);
      toast.error(getFriendlyApiError(err, "Could not move card to LOSS."));
    } finally {
      setLossSubmitting(false);
    }
  }, [handleStaleCardConflict, instanceUniqueId, loadPipeline, lossNotes, lossOutcome, lossReason, lossTransition, workspace.card]);

  const openReviveDialog = useCallback((card) => {
    if (!card || card.pipelineColumn !== "loss") return;
    setReviveReason("");
    setReviveNotes("");
    setReviveTarget(card);
  }, []);

  const closeReviveDialog = useCallback(() => {
    if (reviveSubmitting) return;
    setReviveTarget(null);
    setReviveReason("");
    setReviveNotes("");
  }, [reviveSubmitting]);

  const confirmRevive = useCallback(async () => {
    if (!reviveTarget?.orderId || !reviveReason) return;
    const revivedOrderId = reviveTarget.orderId;
    try {
      setReviveSubmitting(true);
      await updatePipelineStage(revivedOrderId, {
        instanceUniqueId,
        soStatus: "Quote Requested",
        targetColumn: "opportunity",
        revive: true,
        reviveReason,
        reviveNotes,
        expectedStatus: reviveTarget.soStatus,
        expectedStage: reviveTarget.pipelineColumn,
        expectedUpdatedAt: reviveTarget.updatedAt || undefined,
      });
      setReviveTarget(null);
      setReviveReason("");
      setReviveNotes("");
      closeWorkspace();
      toast.success(`Revived #${revivedOrderId} to Opportunity.`);
      await loadPipeline();
    } catch (err) {
      if (await handleStaleCardConflict(err)) {
        setReviveTarget(null);
        setReviveReason("");
        setReviveNotes("");
        closeWorkspace();
        return;
      }
      if (isInternalApiError(err)) console.warn("Revive lead failed:", err);
      toast.error(getFriendlyApiError(err, "Could not revive this lead."));
    } finally {
      setReviveSubmitting(false);
    }
  }, [closeWorkspace, handleStaleCardConflict, instanceUniqueId, loadPipeline, reviveNotes, reviveReason, reviveTarget]);

  const closeFastTransition = useCallback(() => {
    setFastTransition(null);
  }, []);

  const openFastTransitionCart = useCallback((card) => {
    if (!card) return false;
    setFastTransition(null);
    setWorkspace({ card, tab: WORKSPACE_TAB_INDEX.Cart });
    return true;
  }, []);

  const handleFastTransitionSuccess = useCallback(async (result) => {
    setFastTransition(null);
    toast.success(result?.message || "Document created and card moved.");
    await loadPipeline();
  }, [loadPipeline]);

  const handleFastTransitionConflict = useCallback(async (err) => {
    setFastTransition(null);
    await handleStaleCardConflict(err);
  }, [handleStaleCardConflict]);

  const handleUnclaimProspect = useCallback(async (card) => {
    if (!card?.entityId) return;
    if (!window.confirm("Unassign this lead and return it to All Leads?")) return;
    try {
      await unassignProspect(instanceUniqueId, card.entityId, { reason: "Unassigned from CRM board" });
      toast.success("Lead returned to All Leads.");
      closeWorkspace();
      await loadPipeline();
    } catch (err) {
      if (isInternalApiError(err)) console.warn("Prospect unassign failed:", err);
      toast.error(getFriendlyApiError(err, "Could not return prospect."));
    }
  }, [closeWorkspace, instanceUniqueId, loadPipeline]);

  const handleAssignProspect = useCallback(async (card, payload = {}) => {
    if (!card?.entityId) return;
    try {
      await assignProspect(instanceUniqueId, card.entityId, payload);
      toast.success("Prospect assigned.");
      closeWorkspace();
      await loadPipeline();
    } catch (err) {
      if (isInternalApiError(err)) console.warn("Prospect assign failed:", err);
      toast.error(getFriendlyApiError(err, "Could not assign prospect."));
    }
  }, [closeWorkspace, instanceUniqueId, loadPipeline]);

  const handleReassignProspect = useCallback(async (card, payload = {}) => {
    if (!card?.entityId) return;
    try {
      await reassignProspect(instanceUniqueId, card.entityId, payload);
      toast.success("Prospect reassigned.");
      closeWorkspace();
      await loadPipeline();
    } catch (err) {
      if (isInternalApiError(err)) console.warn("Prospect reassign failed:", err);
      toast.error(getFriendlyApiError(err, "Could not reassign prospect."));
    }
  }, [closeWorkspace, instanceUniqueId, loadPipeline]);

  const onSearch = useCallback((event) => {
    event.preventDefault();
    loadPipeline();
  }, [loadPipeline]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0, height: embedded ? "100%" : height, overflow: "hidden" }}>
      {showHeader ? (
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} gap={1.5} sx={{ mb: 1.5 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.1 }}>{isCrmMode ? "CRM Board" : "CRM Pipeline"}</Typography>
            <Typography variant="body2" color="text.secondary">
              {isCrmMode ? "Prospects and orders grouped by CRM stage." : "Orders grouped by sales lifecycle status."}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {isCrmMode && isCrmManager ? (
              <Button
                variant="outlined"
                color="warning"
                onClick={() => setNeedsAttentionOpen(true)}
                startIcon={<WarningAmberRoundedIcon />}
              >
                Needs Attention ({needsAttentionCards.length})
              </Button>
            ) : null}
            {isCrmMode && isCrmManager ? <Button variant="outlined" onClick={() => setManagementReportsOpen(true)}>Reports</Button> : null}
            {isCrmMode ? <Button variant="outlined" onClick={() => setRepAlertsOpen(true)}>Notifications</Button> : null}
            {isCrmMode && isCrmManager ? <Button variant="outlined" onClick={() => setUnmappedReviewOpen(true)}>Unmapped Emails</Button> : null}
            <Box component="form" onSubmit={onSearch}>
              <TextField
                size="small"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={isCrmMode ? "Search prospect, project, customer" : "Search project, customer, email"}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
                sx={{ minWidth: { xs: 220, md: 300 }, "& .MuiOutlinedInput-root": { backgroundColor: "var(--ec-input-bg, #fff)" } }}
              />
            </Box>
            <Button
              variant="outlined"
              onClick={() => loadPipeline()}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={17} /> : <RefreshRoundedIcon />}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
            {!embedded ? <Button variant="outlined" onClick={() => history.push(`/instance/${instanceUniqueId}/sales/orders`)}>Projects</Button> : null}
          </Stack>
        </Stack>
      ) : null}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
        <Chip size="small" label={`${summary?.totalCards ?? summary?.totalCount ?? 0} cards`} />
        <Chip size="small" label={`${summaryValueLabel(summary)} value`} />
        {isCrmMode ? <Chip size="small" variant="outlined" label={`${summary?.prospectCount || 0} prospects`} /> : null}
        {isCrmMode ? <Chip size="small" variant="outlined" label={`${summary?.orderCount || 0} orders`} /> : null}
        {isCrmMode && crmScope === "user" ? <Chip size="small" variant="outlined" label={`Viewing ${selectedUserName || `User #${selectedUserId}`}`} /> : null}
        {isCrmMode ? <Chip size="small" variant="outlined" label="Lanes load independently" /> : <Chip size="small" variant="outlined" label={`Showing first ${pageSize}`} />}
        <Chip size="small" variant="outlined" label={isCrmMode ? "Controlled CRM movement" : "Win/Loss read-only"} />
      </Stack>

      {isCrmMode ? (
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }} alignItems="center">
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>Quick filters:</Typography>
          {ATTENTION_FILTERS.map((filter) => (
            <Chip
              key={filter.key}
              size="small"
              clickable
              color={attentionFilter === filter.key ? "primary" : "default"}
              variant={attentionFilter === filter.key ? "filled" : "outlined"}
              label={filter.label}
              onClick={() => setAttentionFilter((current) => current === filter.key ? "" : filter.key)}
            />
          ))}
          <TextField select SelectProps={{ native: true }} size="small" value={tagFilter} onChange={(event) => setTagFilter(event.target.value)} sx={{ minWidth: 150, "& .MuiInputBase-root": { height: 28 }, "& select": { py: 0.25, fontSize: 13 } }}>
            <option value="">All tags</option>
            {tagDefinitions.map((tag) => <option key={tag.id} value={tag.id}>{tag.name}</option>)}
          </TextField>
          {attentionFilter || tagFilter ? <Typography variant="caption" color="text.secondary">Drag is disabled while filtering.</Typography> : null}
        </Stack>
      ) : null}

      {error ? <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert> : null}
      {isCrmMode ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Managers assign leads from All Leads. Sales users work assigned or self-created leads in My Leads before conversion or project creation.
        </Alert>
      ) : null}

      <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: "hidden" }}>
        {loading && !summary ? (
          <LoadingLanes />
        ) : (
          <Box
            ref={boardScrollerRef}
            sx={boardSx}
            data-pipeline-board-scroller="true"
            onWheel={(event) => {
              if (event.shiftKey && Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
                const scroller = event.currentTarget;
                const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
                if (maxScrollLeft > 0) {
                  const nextScrollLeft = Math.max(0, Math.min(maxScrollLeft, scroller.scrollLeft + event.deltaY));
                  if (nextScrollLeft !== scroller.scrollLeft) {
                    scroller.scrollLeft = nextScrollLeft;
                    event.preventDefault();
                  }
                }
              }
            }}
          >
            {columns.map((column) => {
              const group = visibleGroups[column.key] || fallbackGroups[column.key];
              return (
                <PipelineLane
                  key={column.key}
                  column={column}
                  group={group}
                  isDragOver={dragOverColumn === column.key}
                  onNativeLaneDragOver={handleNativeLaneDragOver}
                  onNativeLaneDrop={handleNativeDrop}
                  onLoadMore={() => loadMoreLane(column.key)}
                  loadingMore={Boolean(laneLoading[column.key])}
                  loadError={laneErrors[column.key] || ""}
                  onRetry={() => loadMoreLane(column.key)}
                >
                  {column.key === "win" ? (
                    WIN_STATUSES.map((status) => {
                      const section = group.sections?.[status] || { cards: [], count: 0 };
                      return (
                        <Box key={status}>
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 0.5, py: 0.6 }}>
                            <Typography variant="caption" sx={{ fontWeight: 900 }}>{status}</Typography>
                            <Typography variant="caption" color="text.secondary">{section.count || 0}</Typography>
                          </Stack>
                          <Stack spacing={1}>
                            {(section.cards || []).map((card, index) => (
                              <PipelineCard
                                key={cardDomId(card)}
                                card={card}
                                columnKey={column.key}
                                index={index}
                                isDragging={String(draggingId) === String(cardDomId(card))}
                                isCrmMode={isCrmMode}
                                onOpenTab={openWorkspace}
                                onUnclaimProspect={handleUnclaimProspect}
                                currentUserId={currentUserId}
                                isCrmManager={isCrmManager}
                                disableDragging={Boolean(attentionFilter || tagFilter)}
                                onNativeDragStart={handleNativeDragStart}
                                onNativeCardDragOver={handleNativeCardDragOver}
                                onNativeDragEnd={clearNativeDrag}
                              />
                            ))}
                          </Stack>
                        </Box>
                      );
                    })
                  ) : (group.cards || []).length ? (
                    (group.cards || []).map((card, index) => (
                      <PipelineCard
                        key={cardDomId(card)}
                        card={card}
                        columnKey={column.key}
                        index={index}
                        isDragging={String(draggingId) === String(cardDomId(card))}
                        isCrmMode={isCrmMode}
                        onOpenTab={openWorkspace}
                        onUnclaimProspect={handleUnclaimProspect}
                        currentUserId={currentUserId}
                        isCrmManager={isCrmManager}
                        disableDragging={Boolean(attentionFilter || tagFilter)}
                        onNativeDragStart={handleNativeDragStart}
                        onNativeCardDragOver={handleNativeCardDragOver}
                        onNativeDragEnd={clearNativeDrag}
                      />
                    ))
                  ) : <EmptyLane />}
                </PipelineLane>
              );
            })}
          </Box>
        )}
      </Box>

      <WorkspaceModal
        card={workspace.card}
        initialTab={workspace.tab}
        onClose={closeWorkspace}
        instanceUniqueId={instanceUniqueId}
        onAssignProspect={handleAssignProspect}
        onReassignProspect={handleReassignProspect}
        onUnclaimProspect={handleUnclaimProspect}
        onRefreshBoard={loadPipeline}
        onReviveLoss={openReviveDialog}
        currentUserId={currentUserId}
        isCrmManager={isCrmManager}
        users={users}
        availableTags={tagDefinitions}
      />
      {isCrmMode && isCrmManager ? (
        <NeedsAttentionDialog
          open={needsAttentionOpen}
          onClose={() => setNeedsAttentionOpen(false)}
          cards={needsAttentionCards}
          onOpenCard={openAttentionCard}
          scopeLabel={attentionScopeLabel}
          availableTags={tagDefinitions}
        />
      ) : null}
      {isCrmMode && isCrmManager ? <ManagementReportsDialog open={managementReportsOpen} onClose={() => setManagementReportsOpen(false)} cards={allBoardCards} scopeLabel={attentionScopeLabel} /> : null}
      {isCrmMode ? (
        <NotificationCandidatesDialog
          open={repAlertsOpen}
          onClose={() => setRepAlertsOpen(false)}
          cards={allBoardCards}
          onOpenCard={openReportCard}
          instanceUniqueId={instanceUniqueId}
          canGenerate={isCrmManager}
        />
      ) : null}
      {isCrmMode && isCrmManager ? (
        <UnmappedCommunicationsDialog
          open={unmappedReviewOpen}
          onClose={() => setUnmappedReviewOpen(false)}
          instanceUniqueId={instanceUniqueId}
          onResolved={loadPipeline}
        />
      ) : null}
      <FastUpdateDialog
        open={Boolean(fastTransition)}
        instanceUniqueId={instanceUniqueId}
        transition={fastTransition}
        onClose={closeFastTransition}
        onSuccess={handleFastTransitionSuccess}
        onConflict={handleFastTransitionConflict}
        onOpenCart={openFastTransitionCart}
      />
      <Dialog open={Boolean(lossTransition)} onClose={closeLossTransition} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Mark as Lost / Cancelled</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              select
              SelectProps={{ native: true }}
              label="Outcome"
              value={lossOutcome}
              onChange={(event) => setLossOutcome(event.target.value)}
              fullWidth
            >
              <option value="Lost">Lost</option>
              <option value="Cancelled">Cancelled</option>
            </TextField>
            <TextField
              select
              SelectProps={{ native: true }}
              label="Reason"
              value={lossReason}
              onChange={(event) => setLossReason(event.target.value)}
              fullWidth
              required
            >
              <option value="">Select reason</option>
              {LOSS_REASONS.map((reason) => <option key={reason} value={reason}>{reason}</option>)}
            </TextField>
            <TextField
              label="Notes"
              value={lossNotes}
              onChange={(event) => setLossNotes(event.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeLossTransition} disabled={lossSubmitting}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmLossTransition}
            disabled={lossSubmitting || !lossReason}
          >
            {lossSubmitting ? "Moving..." : "Move to LOSS"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(reviveTarget)} onClose={closeReviveDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 900 }}>Revive lead</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              label="Target status"
              value="Opportunity"
              InputProps={{ readOnly: true }}
              fullWidth
            />
            <TextField
              select
              SelectProps={{ native: true }}
              label="Reason"
              value={reviveReason}
              onChange={(event) => setReviveReason(event.target.value)}
              fullWidth
              required
            >
              <option value="">Select reason</option>
              {REVIVE_REASONS.map((reason) => <option key={reason} value={reason}>{reason}</option>)}
            </TextField>
            <TextField
              label="Notes"
              value={reviveNotes}
              onChange={(event) => setReviveNotes(event.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeReviveDialog} disabled={reviveSubmitting}>Cancel</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={confirmRevive}
            disabled={reviveSubmitting || !reviveReason}
          >
            {reviveSubmitting ? "Reviving..." : "Revive to Opportunity"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const truncate = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const pillSx = {
  display: "inline-flex",
  alignItems: "center",
  minWidth: 0,
  height: 22,
  px: 0.75,
  borderRadius: 1,
  border: "1px solid",
  fontSize: 11,
  fontWeight: 850,
  lineHeight: "20px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const metricSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.35,
  height: 22,
  px: 0.7,
  borderRadius: 1,
  border: "1px solid rgba(148, 163, 184, 0.36)",
  backgroundColor: "#fff",
  color: "#475569",
  fontSize: 11,
  fontWeight: 750,
  lineHeight: "20px",
};

const actionButtonSx = {
  minHeight: 28,
  textTransform: "none",
  fontWeight: 800,
  borderRadius: 1.5,
  cursor: "pointer",
};

const scrollbarSx = {
  "&::-webkit-scrollbar": { width: 7, height: 7 },
  "&::-webkit-scrollbar-thumb": { backgroundColor: "rgba(148, 163, 184, 0.72)", borderRadius: 99 },
  "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
};

const boardSx = {
  height: "100%",
  minHeight: 0,
  minWidth: 0,
  display: "grid",
  gridAutoFlow: "column",
  gridAutoColumns: "minmax(292px, 1fr)",
  alignItems: "stretch",
  gap: 1.5,
  overflowX: "auto",
  overflowY: "hidden",
  overscrollBehaviorX: "contain",
  overscrollBehaviorY: "auto",
  pb: 1,
  pr: 0.5,
  ...scrollbarSx,
};

export default memo(PipelineBoard);
